import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { T, knopfStil, primaerKnopfStil } from '../../lib/designTokens';
import { Aufklapper, textKnopfStil } from './abwaegenAnzeige';
import { CheckCircle2, Save, RotateCcw, Play, Link2, Download, Printer, Archive, X as XIcon, ListChecks, Upload, Pencil, Plus, Trash2, Users, Image as ImageIcon, ChevronDown, ChevronRight } from 'lucide-react';
import { mergeMissingAnlage2, normalizeDachverpachtungPartyLabels } from '../../lib/contractMerging';
import {
  applySourceHtmlToTemplate,
  buildContractStateKey,
  disableEditorInHtml,
  injectBaseHref,
  buildLegacyContractStateKey,
  hasStoredContractContent,
  ladeVertragsVorlage,
  standNichtLadbar,
  vertragsAbschnitteAus,
  // Seit dem 23.09.2026 in src/lib - die mobile Oberfläche baut die Nutzlast
  // mit denselben Funktionen.
  CONTRACTS_AM_URL,
  LAGEPLAN_PLACEHOLDER,
  LAGEPLAN_PLACEHOLDER_TOKEN,
  MAX_CONTRACT_ATTACHMENT_BYTES,
  applyBuyerManualFieldsToHtml,
  applyUploadedContractToVariantTemplate,
  buildMappingFieldsWithClauseDefaults,
  cleanAdditionalOwnersForPayload,
  createAdditionalOwnerDraft,
  createAttachmentId,
  extractAnnexReferencesFromHtml,
  extractClausePlaceholders,
  extractPlaceholders,
  getLageplanImageInfo,
  hasBuyerValues,
  kickManualUploadWorker,
  loadAnnexContractMappingFields,
  loadAnnexContractVariants,
  mergePlaceholders,
  normalizeAttachmentForPayload,
  normalizeBuyerDraft,
  normalizeClauseDefaultMapping,
  readFileAsBase64,
  readImageFileAsDataUrl,
  rememberManualUploadBatch,
  removeBuyerFieldsFromMapping,
  replaceLageplanImageSource,
} from '../../lib/vertragsStand';
// Erkennen, Laden und Bereinigen eines hochgeladenen Vertrags teilt sich das
// Studio seit dem 23.09.2026 mit der mobilen Oberfläche (reines Verschieben).
import {
  EDITOR_LAEDT_NOCH,
  detectContractFromHtml,
  editorBereit,
  leseKundenvertragAusRahmen,
  prepareCustomerEditableHtml,
  seitenvorlageAus,
  seitenvorlageFehltGanz,
  vertragUnvollstaendig,
} from '../../lib/vertragBearbeiten';
import { istKaeuferFluss } from '../../lib/vertragErstellen';
import {
  belegteFelder,
  fruehereStaende,
  lageplanBildZuordnen,
  mappingMitVorschlaegen,
  mappingSpeichernWarnung,
  mappingVorschlaegeFuer,
  mappingZeilen,
  zuzuordnendePlatzhalter,
} from '../../lib/vertragsMapping';
import { ersetzungenInVarianten, ersetzungsFehlerText, gespraechsReferenz } from '../../lib/gespraech';
import { lueckenSperrText, offeneGespraechsLuecken } from '../../lib/luecken';
import { refreshMappedFieldsInEditedHtml } from '../../lib/dealDatenAktualisieren';
import { machVertragsHtmlEigenstaendig, NUR_ONLINE_HINWEIS } from '../../lib/eigenstaendigesHtml';
import VerhandlungsPassus from './VerhandlungsPassus';
import AnlagenAuswahl from './AnlagenAuswahl';
import AufhebungAngaben from './AufhebungAngaben';
import { LEERE_AUFHEBUNG, heuteIso, istKuendigung, istVertragsende, vertragsName, vertragsendeVollstaendig, wendeVertragsendeAn } from '../../lib/aufhebung';
import WerkvertragAngaben from './WerkvertragAngaben';
import { KAEUFER_FELDNAMEN, kaeuferAusText } from '../../lib/kaeuferAusText';
import {
  AUFTRAGGEBER_FELDER, LEERE_WERKVERTRAG_ANGABEN, NICHT_HOCHLADEN_HINWEIS, enthaeltEegBaustein, grundzuordnungFuer, istWerkvertrag, mitGrundzuordnung, schalteEegAbsicherung, waehleVerkaeufer, wendeWerkvertragAn,
} from '../../lib/werkvertrag';
import {
  anlagenNummern,
  anlagenVerweise,
  bitrixDateienJeNummer,
  felderDerAnlagen,
  standardAuswahl,
  teileBausteine,
} from '../../lib/dealAnlagenAuswahl';
import {
  KLEINSPEICHER_LEASE_ANNEX_KEY,
  KLEINSPEICHER_LEASE_ANNEX_NUMBER,
  KLEINSPEICHER_LEASE_CONTRACT_KEY_EZ,
  KLEINSPEICHER_LEASE_CONTRACT_KEY_JZ,
  KLEINSPEICHER_PURCHASE_CONTRACT_KEY,
  LEASE_NUMBER_INITIAL_VALUES,
  buildLeaseNumberMappingFields,
  getLeaseNumberRequirements,
  getMissingLeaseNumberLabels,
  mergeMappingFieldsPreferFilled,
} from '../../lib/kleinspeicherLeaseAnnex';

const CONTRACTS = [
  {
    key: 'kaufvertrag-kleinspeicher',
    label: 'Kaufvertrag Kleinspeicher / Schwarmspeicher',
    variants: [
      { key: 'ci', label: 'CI', file: 'Kaufvertrag_Kleinspeicher_Schwarmspeicher_ENPOWER_CI.html' },
      { key: 'druckversion', label: 'Druckversion', file: 'Kaufvertrag_Kleinspeicher_Schwarmspeicher_ENPOWER_CI_Druckversion.html' },
    ],
  },
  {
    key: 'projektrechte-pv',
    label: 'Projektrechte PV Kaufvertrag',
    variants: [
      { key: 'ci', label: 'CI', file: 'Projektrechte_PV_ENPOWER_CI.html' },
      { key: 'druckversion', label: 'Druckversion', file: 'Projektrechte_PV_ENPOWER_CI_Druckversion.html' },
    ],
  },
  {
    key: 'dachnutzungsvertrag-sanierung',
    label: 'Dachnutzungsvertrag Sanierung',
    variants: [
      { key: 'ci', label: 'CI', file: 'Dachnutzungsvertrag_Sanierung_ENPOWER_CI.html' },
      { key: 'druckversion', label: 'Druckversion', file: 'Dachnutzungsvertrag_Sanierung_ENPOWER_CI_Druckversion.html' },
    ],
  },
  {
    key: 'pachtvertrag-dachverpachtung-ez',
    label: 'Pachtvertrag Dachverpachtung (Einmalzahlung)',
    variants: [
      { key: 'ci', label: 'CI', file: 'Pachtvertrag_Dachverpachtung_EZ_ENPOWER_CI.html' },
      { key: 'druckversion', label: 'Druckversion', file: 'Pachtvertrag_Dachverpachtung_EZ_ENPOWER_CI_Druckversion.html' },
    ],
  },
  {
    key: 'pachtvertrag-dachverpachtung-jz',
    label: 'Pachtvertrag Dachverpachtung (Jahreszahlung)',
    variants: [
      { key: 'ci', label: 'CI', file: 'Pachtvertrag_Dachverpachtung_JZ_ENPOWER_CI.html' },
      { key: 'druckversion', label: 'Druckversion', file: 'Pachtvertrag_Dachverpachtung_JZ_ENPOWER_CI_Druckversion.html' },
    ],
  },
  {
    key: 'pachtvertrag-kleinspeicher-ez',
    label: 'Pachtvertrag Kleinspeicher (Einmalzahlung)',
    variants: [
      { key: 'ci', label: 'CI', file: 'Pachtvertrag_Kleinspeicher_EZ_ENPOWER_CI.html' },
      { key: 'druckversion', label: 'Druckversion', file: 'Pachtvertrag_Kleinspeicher_EZ_ENPOWER_CI_Druckversion.html' },
    ],
  },
  {
    key: 'pachtvertrag-kleinspeicher-jz',
    label: 'Pachtvertrag Kleinspeicher (Jahreszahlung)',
    variants: [
      { key: 'ci', label: 'CI', file: 'Pachtvertrag_Kleinspeicher_JZ_ENPOWER_CI.html' },
      { key: 'druckversion', label: 'Druckversion', file: 'Pachtvertrag_Kleinspeicher_JZ_ENPOWER_CI_Druckversion.html' },
    ],
  },
  {
    key: 'pachtvertrag-grossspeicher-ez',
    label: 'Pachtvertrag Großspeicher (Einmalzahlung)',
    variants: [
      { key: 'ci', label: 'CI', file: 'Pachtvertrag_Grossspeicher_EZ_ENPOWER_CI.html' },
      { key: 'druckversion', label: 'Druckversion', file: 'Pachtvertrag_Grossspeicher_EZ_ENPOWER_CI_Druckversion.html' },
    ],
  },
  {
    key: 'pachtvertrag-grossspeicher-jz',
    label: 'Pachtvertrag Großspeicher (Jahreszahlung)',
    variants: [
      { key: 'ci', label: 'CI', file: 'Pachtvertrag_Grossspeicher_JZ_ENPOWER_CI.html' },
      { key: 'druckversion', label: 'Druckversion', file: 'Pachtvertrag_Grossspeicher_JZ_ENPOWER_CI_Druckversion.html' },
    ],
  },
  {
    key: 'aufhebungsvereinbarung',
    label: 'Aufhebungsvereinbarung',
    variants: [
      { key: 'ci', label: 'CI', file: 'Aufhebungsvereinbarung_ENPOWER_CI.html' },
      { key: 'druckversion', label: 'Druckversion', file: 'Aufhebungsvereinbarung_ENPOWER_CI_Druckversion.html' },
    ],
  },
  {
    key: 'kuendigung-eigentuemervertrag',
    label: 'Kündigung Eigentümervertrag',
    variants: [
      { key: 'ci', label: 'CI', file: 'Kuendigung_Eigentuemervertrag_ENPOWER_CI.html' },
      { key: 'druckversion', label: 'Druckversion', file: 'Kuendigung_Eigentuemervertrag_ENPOWER_CI_Druckversion.html' },
    ],
  },
  {
    // Neutrale Vorlage (30.10.2026): EN:POWER ist nicht Partei, daher ohne
    // Logo - die Variante heisst "Standard", der Schluessel bleibt 'ci'.
    key: 'werkliefervertrag-pv',
    label: 'Werkliefervertrag PV',
    variants: [
      { key: 'ci', label: 'Standard', file: 'Werkliefervertrag_PV.html' },
      { key: 'druckversion', label: 'Druckversion', file: 'Werkliefervertrag_PV_Druckversion.html' },
    ],
  },
];

const BUYER_LIBRARY_STORAGE_KEY = 'enpower:contracts-am:buyerPartners:v1';
// Kleiner wird die Vorschau-Seite nicht - darunter waere sie nicht mehr lesbar.
const VORSCHAU_ZOOM_MIN = 0.3;
const EMPTY_BUYER_DRAFT = {
  id: '',
  label: '',
  company: '',
  street: '',
  postalCity: '',
  registerCourt: '',
  hrb: '',
  representatives: '',
  phone: '',
  mobile: '',
  email: '',
  vatId: '',
  taxNumber: '',
  legalForm: '',
  seat: '',
};
const DEFAULT_BUYER_PARTNERS = [
  {
    id: 'default-wolf-energy-gmbh',
    label: 'Wolf Energy GmbH',
    company: 'Wolf Energy GmbH',
    street: 'Oberdorf 7',
    postalCity: '06313 Wimmelburg',
    registerCourt: 'Amtsgericht Stendal',
    hrb: 'HRB 22215',
    representatives: 'Bernd Stefan Wolf',
    phone: '+49 (0) 3475 - 77 39 12 1',
    mobile: '+49 (0) 177 - 78 43 48 5',
    email: 'info@wolf-energy.de',
    vatId: 'DE 302111329',
    taxNumber: '118/105/47032',
    legalForm: 'GmbH',
    seat: 'Oberdorf 7, 06313 Wimmelburg',
  },
];

function createBuyerId() {
  return `buyer-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function loadBuyerLibrary() {
  if (typeof window === 'undefined') return [];
  try {
    const parsed = JSON.parse(window.localStorage.getItem(BUYER_LIBRARY_STORAGE_KEY) || '[]');
    const merged = [...(Array.isArray(parsed) ? parsed : []), ...DEFAULT_BUYER_PARTNERS];
    const seen = new Set();
    return merged
      .map(normalizeBuyerDraft)
      .filter((buyer) => buyer.id && (buyer.label || buyer.company))
      .filter((buyer) => {
        if (seen.has(buyer.id)) return false;
        seen.add(buyer.id);
        return true;
      });
  } catch (_error) {
    return DEFAULT_BUYER_PARTNERS.map(normalizeBuyerDraft);
  }
}

function persistBuyerLibrary(nextLibrary) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(BUYER_LIBRARY_STORAGE_KEY, JSON.stringify(nextLibrary || []));
  } catch (_error) {
    // Standardkunden bleiben Komfortdaten; der Vertrag selbst wird dadurch nicht blockiert.
  }
}

function getBuyerDisplayName(buyer) {
  return String(buyer?.label || buyer?.company || buyer?.representatives || 'Standardkunde').trim();
}

function notifyManualUploadQueued(callback, payload) {
  if (typeof callback !== 'function') return;
  if (typeof window !== 'undefined' && typeof window.setTimeout === 'function') {
    window.setTimeout(() => callback(payload), 0);
    return;
  }
  callback(payload);
}

function getContractDefinition(contractKey) {
  return CONTRACTS.find((contract) => contract.key === String(contractKey || '').trim()) || null;
}

function getVariantDefinition(contract, variantKey) {
  if (!contract) return null;
  return contract.variants.find((variant) => variant.key === String(variantKey || '').trim())
    || contract.variants[0]
    || null;
}


const FIELD_HINTS = [
  'DEAL.TITLE',
  'DEAL.UF_CRM_1749573387243',
  'DEAL.OPPORTUNITY',
  'CONTACT.NAME',
  'CONTACT.LAST_NAME',
  'CONTACT.EMAIL',
  'CONTACT.PHONE',
  'COMPANY.TITLE',
  'COMPANY.ADDRESS',
];

// Entwurfssicherung: localStorage fasst etwa 5 MB (UTF-16), dazu kommen andere Einträge.
const ENTWURF_HOECHSTENS_ZEICHEN = 4000000;

// Beschriftung ueber einem Feld - normale Schrift statt der gesperrten
// Versalien (Ueberarbeitung 23.09.2026).
const FELD_BESCHRIFTUNG = { fontSize: T.tMikro, fontWeight: 700, color: T.muted };

export default function VertragsAutomationStudio({ apiBase = '/api', session = null, initialDealId = '', initialContractKey = '', mode = 'create', onManualUploadQueued = null }) {
  const accessToken = session?.access_token || null;
  const isAuthenticated = Boolean(accessToken);
  const isExistingContractEditMode = mode === 'edit-existing';

  const [activeContractKey, setActiveContractKey] = useState(CONTRACTS[0].key);
  const [activeVariantKey, setActiveVariantKey] = useState(CONTRACTS[0].variants[0].key);
  // PDF-Anlagen stehen am Ende der Seitenleiste und sind standardmäßig eingeklappt.
  const [attachmentsOpen, setAttachmentsOpen] = useState(false);
  // Das Felder-Mapping ist Einrichtung, keine Tagesarbeit - eingeklappt wie
  // die Anlagen (seit 23.09.2026; vorher stand es immer offen, mit den
  // Bitrix-Kennungen jedes Platzhalters).
  const [mappingOpen, setMappingOpen] = useState(false);
  const [lageplanFeldOpen, setLageplanFeldOpen] = useState(false);
  // Anlage 8 ist im Kaufvertrag fest vorgesehen, deshalb standardmaessig an.
  const [leaseAnnexEnabled, setLeaseAnnexEnabled] = useState(true);
  const [leaseAnnexContractKey, setLeaseAnnexContractKey] = useState(KLEINSPEICHER_LEASE_CONTRACT_KEY_JZ);
  const [leaseAnnexNumbers, setLeaseAnnexNumbers] = useState(LEASE_NUMBER_INITIAL_VALUES);
  const [iframeKey, setIframeKey] = useState(0);
  const [iframeSrc, setIframeSrc] = useState(null);
  const [iframeSrcDoc, setIframeSrcDoc] = useState(null);

  const [templateHtml, setTemplateHtml] = useState('');
  const [templateSource, setTemplateSource] = useState('original');
  // Gespeicherte Vorlage nicht abrufbar (seit 09.10.2026): dann kein Rueckfall auf die
  // Originaldatei, sondern dieser Text im Hinweisband.
  const [vorlagenFehler, setVorlagenFehler] = useState('');
  const [placeholders, setPlaceholders] = useState([]);
  const [mappingDraft, setMappingDraft] = useState({});
  const [mappingSavedAt, setMappingSavedAt] = useState(null);
  // Schutz vor einem geleerten Mapping (05.10.2026): Der gespeicherte Stand, gegen
  // den beim Speichern verglichen wird, und für welchen Vertrag er geladen ist.
  // Solange nichts (oder ein anderer Vertrag) geladen ist, bleibt Speichern gesperrt –
  // contracts-am ersetzt das Mapping vollständig, ein leerer Entwurf leerte alles.
  const [mappingGespeichert, setMappingGespeichert] = useState(null);
  const [mappingGeladenFuer, setMappingGeladenFuer] = useState(null);
  const [mappingLadefehler, setMappingLadefehler] = useState(false);
  const [fruehereStaendeOffen, setFruehereStaendeOffen] = useState(false);
  const [fruehereStaendeListe, setFruehereStaendeListe] = useState(null);
  const [fruehereStaendeLaden, setFruehereStaendeLaden] = useState(false);
  const [standWirdWiederhergestellt, setStandWirdWiederhergestellt] = useState(null);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [saving, setSaving] = useState(false);

  const [dealId, setDealId] = useState('');
  const [previewing, setPreviewing] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [previewSummary, setPreviewSummary] = useState(null);
  const [unresolved, setUnresolved] = useState([]);
  const [filledHtml, setFilledHtml] = useState(null);
  const [htmlEditMode, setHtmlEditMode] = useState(false);
  const [uploadedHtmlName, setUploadedHtmlName] = useState('');
  // Entwurfssicherung: Kundenvertraege leben sonst nur im Seitenspeicher und
  // sind nach einem Neuladen des Tools verloren.
  const ENTWURF_KEY = 'enpower-bearbeitung-entwurf';
  const [entwurf, setEntwurf] = useState(null);
  const [additionalOwnersDraft, setAdditionalOwnersDraft] = useState([]);
  const [availableClauses, setAvailableClauses] = useState([]);
  const [selectedClauseIds, setSelectedClauseIds] = useState([]);
  // Anlagen aus dem Deal (seit 26.10.2026): Dateien der Bitrix-Felder der
  // Anlagen-Bausteine und die Wahl von Dateien und Seiten je Anlage.
  const [dealDateien, setDealDateien] = useState(null);
  const [dealDateienLaden, setDealDateienLaden] = useState(false);
  const [dealDateienFehler, setDealDateienFehler] = useState('');
  const [dealDateienAnstoss, setDealDateienAnstoss] = useState(0);
  const [anlagenAuswahl, setAnlagenAuswahl] = useState({});
  // Die Standardauswahl kommt je Deal und Vertrag einmal - danach entscheidet der Bearbeiter.
  const standardAngewandtFuer = useRef('');
  // Passus aus dem Kundengespraech. Sie stehen bewusst NEBEN der Bibliothek:
  // Sie gehoeren zu diesem einen Vorgang und werden nicht dauerhaft abgelegt.
  // Freigegeben werden sie in VerhandlungsPassus, hier reisen sie nur mit.
  const [verhandlungsKlauseln, setVerhandlungsKlauseln] = useState([]);
  // Zugestimmte Ersetzungen aus dem Gespräch. Beim Erstellen kommen sie beim
  // Füllen und Abschließen in die Vorlage beider Varianten (seit 24.10.2026).
  const [gespraechsErsetzungen, setGespraechsErsetzungen] = useState([]);
  const [uploadedContractInfo, setUploadedContractInfo] = useState(null);
  const [iframeHeight, setIframeHeight] = useState(900);
  const [lageplanApplying, setLageplanApplying] = useState(false);
  const [refreshingMappedFields, setRefreshingMappedFields] = useState(false);
  const [uploadSiblingVariantForHtml, setUploadSiblingVariantForHtml] = useState(true);
  const [buyerLibrary, setBuyerLibrary] = useState(() => loadBuyerLibrary());
  const [selectedBuyerId, setSelectedBuyerId] = useState('');
  const [buyerDraft, setBuyerDraft] = useState(() => ({ ...EMPTY_BUYER_DRAFT }));
  // Eingefügter Text für die Käufererkennung (src/lib/kaeuferAusText.js) und ihr Ergebnis.
  const [kaeuferText, setKaeuferText] = useState('');
  const [kaeuferErkennung, setKaeuferErkennung] = useState(null);
  // Aufhebungsvereinbarung und Kündigung: Angaben zum beendeten Vertrag (src/lib/aufhebung.js).
  const [aufhebungAngaben, setAufhebungAngaben] = useState(LEERE_AUFHEBUNG);
  const [aufhebungHinweis, setAufhebungHinweis] = useState('');
  // Werkliefervertrag: Angebotsdatum (src/lib/werkvertrag.js).
  const [werkvertragAngaben, setWerkvertragAngaben] = useState(LEERE_WERKVERTRAG_ANGABEN);
  const [contractAttachments, setContractAttachments] = useState([]);

  const iframeRef = useRef(null);
  const fileInputRef = useRef(null);
  const lageplanFileInputRef = useRef(null);
  const templateLoadIdRef = useRef(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const authHeaders = useMemo(() => (
    accessToken ? { Authorization: `Bearer ${accessToken}` } : {}
  ), [accessToken]);
  // Für die Ladeeffekte (Vorlage, Mapping): Supabase erneuert das Token etwa
  // stündlich. Hing das Laden an authHeaders, lud jede Erneuerung die Vorlage
  // neu - gefüllte Vorschau, bearbeiteter Vertrag und ungespeichertes Mapping
  // waren mitten in der Arbeit weg (Befund 24.10.2026).
  const authHeadersRef = useRef(authHeaders);
  authHeadersRef.current = authHeaders;

  const resizePreviewIframe = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    try {
      const doc = iframe.contentDocument;
      if (!doc) return;
      const root = doc.documentElement;
      const pages = doc.getElementById('contract-pages');
      const source = doc.getElementById('contract-source');
      // An die Breite anpassen (seit 08.10.2026, Nutzer: „ein Teil ist
      // verdeckt“): Ist die Vorschau schmaler als die A4-Seite (794 px) - etwa
      // mit offener Gesprächsspalte -, wird die Seite verkleinert statt rechts
      // abgeschnitten; das iframe hat keinen Bildlauf.
      root.style.zoom = '';
      const natuerlicheBreite = Math.max(Number(root.scrollWidth || 0), Number(doc.body?.scrollWidth || 0));
      const verfuegbar = iframe.clientWidth;
      const faktor = verfuegbar > 0 && natuerlicheBreite > verfuegbar
        ? Math.max(VORSCHAU_ZOOM_MIN, (verfuegbar - 8) / natuerlicheBreite)
        : 1;
      if (faktor < 1) root.style.zoom = String(faktor);
      // Ohne documentElement.scrollHeight: Das ist nie kleiner als das iframe
      // selbst, die Vorschau konnte so nur wachsen. Die Inhaltshoehen zaehlen
      // unverkleinert - deshalb mal faktor.
      const inhalt = Math.max(
        Number(doc.body?.scrollHeight || 0),
        Number(pages?.scrollHeight || 0),
        Number(source?.scrollHeight || 0),
      );
      setIframeHeight(Math.max(900, Math.ceil(inhalt * faktor)) + 32);
    } catch (_error) {
      // Cross-origin src iframes keep their own scroll height.
      setIframeHeight(900);
    }
  }, []);

  // Aendert sich die Breite der Vorschau (Fenster, Gesprächsspalte auf/zu),
  // wird neu angepasst. Nur die Breite - die Hoehe setzt resizePreviewIframe.
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe || typeof ResizeObserver === 'undefined') return undefined;
    let breite = iframe.clientWidth;
    let rahmen = 0;
    const beobachter = new ResizeObserver(() => {
      if (iframe.clientWidth === breite) return;
      breite = iframe.clientWidth;
      window.cancelAnimationFrame(rahmen);
      rahmen = window.requestAnimationFrame(resizePreviewIframe);
    });
    beobachter.observe(iframe);
    return () => {
      beobachter.disconnect();
      window.cancelAnimationFrame(rahmen);
    };
  }, [iframeKey, iframeSrc, iframeSrcDoc, resizePreviewIframe]);

  const handlePreviewIframeLoad = useCallback(() => {
    [60, 240, 700, 1400].forEach((delay) => {
      window.setTimeout(resizePreviewIframe, delay);
    });
  }, [resizePreviewIframe]);

  useEffect(() => {
    const timers = [120, 520, 1200].map((delay) => window.setTimeout(resizePreviewIframe, delay));
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [iframeKey, iframeSrc, iframeSrcDoc, resizePreviewIframe]);

  useEffect(() => {
    const nextDealId = String(initialDealId || '').trim();
    if (!nextDealId) return;
    setDealId(nextDealId);
    setPreviewSummary(null);
    setUnresolved([]);
    setFilledHtml(null);
    setHtmlEditMode(false);
    setUploadedHtmlName('');
    setUploadedContractInfo(null);
    setAdditionalOwnersDraft([]);
    setContractAttachments([]);
    setUploadSiblingVariantForHtml(true);
  }, [initialDealId]);

  useEffect(() => {
    const nextContract = CONTRACTS.find((contract) => contract.key === String(initialContractKey || '').trim());
    if (!nextContract) return;
    setActiveContractKey(nextContract.key);
    setActiveVariantKey((currentVariantKey) => (
      nextContract.variants.some((variant) => variant.key === currentVariantKey)
        ? currentVariantKey
        : nextContract.variants[0].key
    ));
  }, [initialContractKey]);

  const activeContract = useMemo(
    () => CONTRACTS.find((c) => c.key === activeContractKey) || CONTRACTS[0],
    [activeContractKey]
  );
  const activeVariant = useMemo(() => {
    const variants = activeContract.variants;
    return variants.find((v) => v.key === activeVariantKey) || variants[0];
  }, [activeContract, activeVariantKey]);

  // Die Abschnittsueberschriften der geladenen Vorlage. Die KI bekommt sie, um
  // einen Passus einem echten Paragraphen zuzuordnen, statt eine Nummer zu
  // raten - und der Server prueft ihren Vorschlag gegen genau diese Liste.
  //
  // Entitaeten muessen dekodiert werden: In den Vorlagen steht "&#167; 13
  // Zusatzvereinbarung", gesucht wird aber "§ 13 Zusatzvereinbarung". Das
  // textarea ist dafuer der harmlose Weg - es liest nur Text heraus und
  // serialisiert nichts zurueck.
  //
  // Wird ein Vertrag bearbeitet (Bereich "Bearbeiten" oder "Bearbeiten" nach
  // dem Fuellen), ist er selbst die Referenz des Gespraechs - nicht die
  // Vorlage (gespraechsReferenz in src/lib/gespraech.js, seit 29.09.2026).
  const gespraech = useMemo(() => gespraechsReferenz({
    bearbeitet: htmlEditMode || isExistingContractEditMode,
    vertragHtml: filledHtml,
    vorlageHtml: templateHtml,
  }), [filledHtml, htmlEditMode, isExistingContractEditMode, templateHtml]);
  const vertragsAbschnitte = useMemo(() => vertragsAbschnitteAus(gespraech.abschnitteHtml), [gespraech.abschnitteHtml]);

  // Springt in der Vorschau zu einem eingefuegten Passus und hebt ihn kurz
  // hervor. Jeder Verhandlungspassus traegt seine Kennung als
  // data-optional-clause-id an JEDEM seiner Elemente (siehe baueKlauselHtml),
  // eine Ersetzung dagegen nicht - dort wird nach dem Wortlaut gesucht.
  //
  // Der Zugriff auf das iframe-Dokument geht, weil die Vorschau ueber srcDoc
  // kommt und damit dieselbe Herkunft hat. Bei einer src-Vorschau von einer
  // anderen Adresse schlaegt er fehl; das wird abgefangen und gemeldet, statt
  // still nichts zu tun.
  //
  // Rueckgabe: true = gefunden, false = nicht gefunden, null = Vorschau nicht
  // lesbar (dazu gibt es immer eine Meldung). Mit { still: true } meldet der
  // Aufrufer "nicht gefunden" selbst - so sucht die Ersetzung erst den neuen,
  // dann den alten Wortlaut (sucheErsetzungImVertrag in src/lib/gespraech.js).
  const springeZuStelle = useCallback((kennung, suchtext = '', { still = false } = {}) => {
    const rahmen = iframeRef.current;
    let dok = null;
    try {
      dok = rahmen?.contentDocument || null;
    } catch (_) {
      dok = null;
    }
    if (!dok) {
      toast.error('Die Vorschau ist nicht lesbar. Erzeuge den Vertrag, dann klappt der Sprung.');
      return null;
    }

    let ziel = kennung
      ? dok.querySelector(`[data-optional-clause-id="${String(kennung).replace(/"/g, '')}"]`)
      : null;

    // Ersetzungen haben keine Kennung: Dort hilft nur der Wortlaut. Gesucht
    // wird der Anfang, weil lange Saetze im Dokument oft umbrochen sind. Sichtbare
    // Treffer zuerst - im Editor steht der Text auch in der versteckten Quelle.
    if (!ziel && suchtext) {
      const anfang = String(suchtext).trim().slice(0, 60).toLowerCase().replace(/\s+/g, ' ');
      if (anfang.length > 12) {
        const treffer = Array.from(dok.querySelectorAll('p, h3, li, td'))
          .filter((el) => (el.textContent || '').toLowerCase().replace(/\s+/g, ' ').includes(anfang));
        ziel = treffer.find((el) => el.getClientRects().length > 0) || treffer[0] || null;
      }
    }

    if (!ziel) {
      if (!still) toast.error('Im Vertrag ist diese Stelle nicht zu finden.');
      return false;
    }

    ziel.scrollIntoView({ behavior: 'smooth', block: 'center' });

    // Kurz aufleuchten lassen. Ohne das weiss man nach dem Scrollen nicht,
    // welcher der Absaetze gemeint war.
    const vorher = ziel.style.cssText;
    ziel.style.transition = 'background-color .25s';
    ziel.style.backgroundColor = '#FEF08A';
    ziel.style.outline = '2px solid #CA8A04';
    ziel.style.outlineOffset = '2px';
    setTimeout(() => { ziel.style.cssText = vorher; }, 2600);
    return true;
  }, []);

  const selectedClauses = useMemo(
    () => [
      ...availableClauses.filter((clause) => selectedClauseIds.includes(clause.id)),
      // Die Gespraechspassus haengen hinten an, damit die Paragraphen aus der
      // Bibliothek ihre gewohnte Reihenfolge behalten. Eingefuegt werden beide
      // gleich - applyOptionalClausesLocally unterscheidet sie nicht.
      ...verhandlungsKlauseln,
    ],
    [availableClauses, selectedClauseIds, verhandlungsKlauseln]
  );

  const { anlagen: anlagenBausteine, paragraphen: paragraphenBausteine } = useMemo(
    () => teileBausteine(availableClauses),
    [availableClauses]
  );
  const anlagenFelder = useMemo(() => felderDerAnlagen(anlagenBausteine), [anlagenBausteine]);
  const anlagenNummernJeBaustein = useMemo(
    () => anlagenNummern(anlagenBausteine, selectedClauseIds),
    [anlagenBausteine, selectedClauseIds]
  );
  const bitrixAnlagenVerweise = useMemo(
    () => anlagenVerweise({ anlagen: anlagenBausteine, gewaehlteIds: selectedClauseIds, auswahl: anlagenAuswahl, dealDateien }),
    [anlagenAuswahl, anlagenBausteine, dealDateien, selectedClauseIds]
  );
  const bitrixDateienProNummer = useMemo(() => bitrixDateienJeNummer(bitrixAnlagenVerweise), [bitrixAnlagenVerweise]);

  const selectedClausePlaceholders = useMemo(() => {
    const groups = [];
    selectedClauses.forEach((clause) => {
      const defaults = normalizeClauseDefaultMapping(clause?.default_mapping || clause?.defaultMapping);
      groups.push(extractClausePlaceholders(clause?.content || ''));
      groups.push(Object.keys(defaults));
    });
    return mergePlaceholders(...groups);
  }, [selectedClauses]);

  const effectivePlaceholders = useMemo(
    () => mergePlaceholders(placeholders, selectedClausePlaceholders),
    [placeholders, selectedClausePlaceholders]
  );

  // Käufer-Ablauf und zuzuordnende Platzhalter seit dem 23.09.2026 aus
  // src/lib (gemeinsam mit der mobilen Mapping-Seite).
  const isBuyerPartnerFlow = useMemo(
    () => istKaeuferFluss(activeContract.key, effectivePlaceholders),
    [activeContract.key, effectivePlaceholders]
  );
  // Lageplan_Bild ist eine Zeile wie jede andere, sobald die Vorlage einen
  // Lageplan-Bereich hat (seit 05.10.2026) - gemessen an der Vorlage, nicht
  // an der gefüllten Vorschau.
  const vorlageMitLageplanBild = useMemo(
    () => lageplanBildZuordnen(getLageplanImageInfo(templateHtml)),
    [templateHtml]
  );
  const automatedMappingPlaceholders = useMemo(
    () => zuzuordnendePlatzhalter(effectivePlaceholders, isBuyerPartnerFlow, { lageplanBild: vorlageMitLageplanBild }),
    [effectivePlaceholders, isBuyerPartnerFlow, vorlageMitLageplanBild]
  );

  // Werkliefervertrag: leere Felder bekommen die feste Grundzuordnung (Fläche, kWp).
  const mappingFieldsForGeneration = useMemo(
    () => mitGrundzuordnung(
      activeContract.key,
      removeBuyerFieldsFromMapping(buildMappingFieldsWithClauseDefaults(mappingDraft, selectedClauses), isBuyerPartnerFlow)
    ),
    [activeContract.key, isBuyerPartnerFlow, mappingDraft, selectedClauses]
  );

  const currentLageplanInfo = useMemo(
    () => getLageplanImageInfo(filledHtml || templateHtml),
    [filledHtml, templateHtml]
  );

  const htmlUploadSiblingVariant = useMemo(
    () => activeContract.variants.find((variant) => variant.key !== activeVariant.key) || null,
    [activeContract.variants, activeVariant.key]
  );

  const additionalOwnersPayload = useMemo(
    () => cleanAdditionalOwnersForPayload(additionalOwnersDraft),
    [additionalOwnersDraft]
  );
  const annexReferences = useMemo(
    () => extractAnnexReferencesFromHtml(filledHtml || templateHtml),
    [filledHtml, templateHtml]
  );
  // Eigene Uploads und die Verweise auf Dateien des Deals; Letztere laedt der Server.
  const attachmentPayload = useMemo(
    () => [...contractAttachments.map(normalizeAttachmentForPayload).filter(Boolean), ...bitrixAnlagenVerweise],
    [bitrixAnlagenVerweise, contractAttachments]
  );
  const attachmentRows = useMemo(() => {
    const annexRows = annexReferences.map((annex) => ({
      ...annex,
      attachment: contractAttachments.find((attachment) => attachment.id === annex.id) || null,
      fromAnnexList: true,
    }));
    const annexIds = new Set(annexReferences.map((annex) => annex.id));
    const looseRows = contractAttachments
      .filter((attachment) => !annexIds.has(attachment.id))
      .map((attachment) => ({
        id: attachment.id,
        anlageNumber: attachment.anlageNumber,
        label: attachment.label,
        attachment,
        fromAnnexList: false,
      }));
    return [...annexRows, ...looseRows];
  }, [annexReferences, contractAttachments]);

  const isKleinspeicherPurchase = activeContract.key === KLEINSPEICHER_PURCHASE_CONTRACT_KEY;
  const leaseAnnexActive = isKleinspeicherPurchase && leaseAnnexEnabled;
  const leaseAnnexRequirements = useMemo(
    () => getLeaseNumberRequirements([leaseAnnexContractKey]),
    [leaseAnnexContractKey]
  );
  const missingLeaseAnnexNumbers = useMemo(
    () => (leaseAnnexActive ? getMissingLeaseNumberLabels(leaseAnnexNumbers, leaseAnnexRequirements) : []),
    [leaseAnnexActive, leaseAnnexNumbers, leaseAnnexRequirements]
  );

  /**
   * Baut den Pachtvertrag als Anlage 8 des Kaufvertrags.
   *
   * Die Feldzuordnung mischt drei Quellen: die des Pachtvertrags (kennt seine
   * eigenen Platzhalter), die des Kaufvertrags (haelt die Deal-Daten, die beide
   * teilen) und zuletzt die von Hand eingetragenen Pachtzahlen. Beide
   * Vorlagenvarianten wandern mit, damit die Warteschlange dieselbe Variante
   * waehlen kann wie fuer den Hauptvertrag.
   */
  const buildLeaseAnnexAttachment = useCallback(async () => {
    if (!leaseAnnexActive) return null;
    const leaseContract = getContractDefinition(leaseAnnexContractKey);
    if (!leaseContract) return null;

    const [leaseVariants, leaseMappingFields] = await Promise.all([
      loadAnnexContractVariants({ apiBase, authHeaders, isAuthenticated, contract: leaseContract }),
      loadAnnexContractMappingFields({ authHeaders, isAuthenticated, contractKey: leaseContract.key }),
    ]);
    const preferred = leaseVariants.find((entry) => entry.variant.key === activeVariant.key) || leaseVariants[0];
    if (!preferred) return null;

    const literalFields = buildLeaseNumberMappingFields(leaseAnnexNumbers, leaseAnnexRequirements);

    return {
      type: 'contract-html',
      key: KLEINSPEICHER_LEASE_ANNEX_KEY,
      contractKey: leaseContract.key,
      variantKey: preferred.variant.key,
      label: leaseContract.label,
      anlageNumber: KLEINSPEICHER_LEASE_ANNEX_NUMBER,
      templateHtml: preferred.html,
      variantTemplates: leaseVariants.map((entry) => ({
        variantKey: entry.variant.key,
        variantLabel: entry.variant.label,
        templateHtml: entry.html,
      })),
      mapping: {
        fields: mergeMappingFieldsPreferFilled(leaseMappingFields, mappingFieldsForGeneration, literalFields),
      },
      literalFields,
    };
  }, [activeVariant.key, apiBase, authHeaders, isAuthenticated, leaseAnnexActive, leaseAnnexContractKey, leaseAnnexNumbers, leaseAnnexRequirements, mappingFieldsForGeneration]);

  const handleAddAdditionalOwner = useCallback(() => {
    setAdditionalOwnersDraft((current) => [...current, createAdditionalOwnerDraft()]);
  }, []);

  const handleRemoveAdditionalOwner = useCallback((ownerId) => {
    setAdditionalOwnersDraft((current) => current.filter((owner) => owner.id !== ownerId));
  }, []);

  const handleAdditionalOwnerChange = useCallback((ownerId, field, value) => {
    setAdditionalOwnersDraft((current) => current.map((owner) => (
      owner.id === ownerId ? { ...owner, [field]: value } : owner
    )));
  }, []);

  // Setzt alle Angaben ein, die der Bearbeiter von Hand macht: Käufer und,
  // bei Aufhebungsvereinbarung und Kündigung, den beendeten Vertrag, beim
  // Werkliefervertrag das Angebotsdatum.
  const istAufhebungsvertrag = istVertragsende(activeContract.key);
  const istWerkliefervertrag = istWerkvertrag(activeContract.key);
  const vertragsendeKey = activeContract.key;
  const applyBuyerFieldsToAutomationHtml = useCallback((html, buyerOverride = buyerDraft) => (
    wendeWerkvertragAn(
      wendeVertragsendeAn(applyBuyerManualFieldsToHtml(html, buyerOverride, isBuyerPartnerFlow), vertragsendeKey, aufhebungAngaben),
      vertragsendeKey,
      werkvertragAngaben
    )
  ), [aufhebungAngaben, buyerDraft, isBuyerPartnerFlow, vertragsendeKey, werkvertragAngaben]);

  const handleAufhebungAendern = useCallback((feld, wert) => {
    setAufhebungAngaben((bisher) => ({ ...bisher, [feld]: wert }));
  }, []);

  const handleWerkvertragAendern = useCallback((feld, wert) => {
    setWerkvertragAngaben((bisher) => ({ ...bisher, [feld]: wert }));
  }, []);

  const handleVerkaeuferWaehlen = useCallback((id) => {
    setWerkvertragAngaben((bisher) => waehleVerkaeufer(bisher, id));
  }, []);

  const handleAuftragnehmerAendern = useCallback((feld, wert) => {
    setWerkvertragAngaben((bisher) => ({ ...bisher, auftragnehmer: { ...bisher.auftragnehmer, [feld]: wert } }));
  }, []);

  const handleEegAbsicherung = useCallback((an) => {
    setWerkvertragAngaben((bisher) => schalteEegAbsicherung(bisher, an));
  }, []);

  const handleBuyerDraftChange = useCallback((field, value) => {
    setBuyerDraft((current) => ({ ...current, [field]: value }));
  }, []);

  const handleSelectBuyer = useCallback((buyerId) => {
    setSelectedBuyerId(buyerId);
    setKaeuferErkennung(null);
    const selected = buyerLibrary.find((buyer) => buyer.id === buyerId);
    if (selected) {
      setBuyerDraft({ ...selected });
    }
  }, [buyerLibrary]);

  const handleResetBuyerDraft = useCallback(() => {
    setSelectedBuyerId('');
    setBuyerDraft({ ...EMPTY_BUYER_DRAFT });
    setKaeuferErkennung(null);
  }, []);

  // Eingefügter Text ersetzt den ganzen Entwurf - sonst blieben Felder des
  // vorigen Käufers stehen, die im neuen Text fehlen.
  const handleKaeuferAusText = useCallback(() => {
    const ergebnis = kaeuferAusText(kaeuferText);
    setKaeuferErkennung(ergebnis);
    if (!ergebnis.erkannt.length) return;
    setSelectedBuyerId('');
    setBuyerDraft({ ...EMPTY_BUYER_DRAFT, ...ergebnis.felder });
    setKaeuferText('');
  }, [kaeuferText]);

  const handleSaveBuyerAsStandard = useCallback(() => {
    const normalized = normalizeBuyerDraft({
      ...buyerDraft,
      id: buyerDraft.id || selectedBuyerId || createBuyerId(),
      label: buyerDraft.label || buyerDraft.company,
    });
    if (!normalized.label && !normalized.company) {
      toast.error('Bitte mindestens einen Namen oder eine Firma für den Standardkunden eintragen.');
      return;
    }
    const nextBuyer = {
      ...normalized,
      id: normalized.id || createBuyerId(),
      label: normalized.label || normalized.company,
    };
    setBuyerLibrary((current) => {
      const next = current.some((buyer) => buyer.id === nextBuyer.id)
        ? current.map((buyer) => (buyer.id === nextBuyer.id ? nextBuyer : buyer))
        : [...current, nextBuyer];
      next.sort((a, b) => getBuyerDisplayName(a).localeCompare(getBuyerDisplayName(b), 'de'));
      persistBuyerLibrary(next);
      return next;
    });
    setSelectedBuyerId(nextBuyer.id);
    setBuyerDraft(nextBuyer);
    toast.success('Standardkunde gespeichert.');
  }, [buyerDraft, selectedBuyerId]);

  const handleDeleteSelectedBuyer = useCallback(() => {
    if (!selectedBuyerId) {
      toast.error('Bitte zuerst einen Standardkunden auswählen.');
      return;
    }
    setBuyerLibrary((current) => {
      const next = current.filter((buyer) => buyer.id !== selectedBuyerId);
      persistBuyerLibrary(next);
      return next;
    });
    handleResetBuyerDraft();
    toast.success('Standardkunde gelöscht.');
  }, [handleResetBuyerDraft, selectedBuyerId]);

  const handleAttachmentFileChange = useCallback(async (annex, file) => {
    if (!file) return;
    const mime = String(file.type || '').toLowerCase();
    const supported = mime === 'application/pdf'
      || mime === 'image/png'
      || mime === 'image/jpeg'
      || /\.(pdf|png|jpe?g)$/i.test(file.name || '');
    if (!supported) {
      toast.error('Bitte für Anlagen nur PDF, PNG oder JPG verwenden.');
      return;
    }
    if (file.size > MAX_CONTRACT_ATTACHMENT_BYTES) {
      toast.error(`Die Anlage ist zu groß. Maximal ${(MAX_CONTRACT_ATTACHMENT_BYTES / 1024 / 1024).toFixed(0)} MB pro Datei.`);
      return;
    }

    try {
      const base64 = await readFileAsBase64(file);
      const nextAttachment = {
        id: annex?.id || createAttachmentId(),
        anlageNumber: Number(annex?.anlageNumber || 0) || null,
        label: String(annex?.label || file.name || 'Anlage').trim(),
        filename: file.name,
        mimeType: file.type || (file.name.toLowerCase().endsWith('.pdf') ? 'application/pdf' : ''),
        bytes: file.size,
        base64,
      };
      setContractAttachments((current) => {
        const exists = current.some((attachment) => attachment.id === nextAttachment.id);
        return exists
          ? current.map((attachment) => (attachment.id === nextAttachment.id ? nextAttachment : attachment))
          : [...current, nextAttachment];
      });
      toast.success(`Anlage gespeichert: ${file.name}`);
    } catch (error) {
      console.error(error);
      toast.error(error?.message || 'Anlage konnte nicht gelesen werden.');
    }
  }, []);

  const handleAttachmentLabelChange = useCallback((attachmentId, field, value) => {
    setContractAttachments((current) => current.map((attachment) => (
      attachment.id === attachmentId
        ? { ...attachment, [field]: field === 'anlageNumber' ? (Number(value) || '') : value }
        : attachment
    )));
  }, []);

  const handleRemoveAttachment = useCallback((attachmentId) => {
    setContractAttachments((current) => current.filter((attachment) => attachment.id !== attachmentId));
  }, []);

  const handleAddLooseAttachment = useCallback(() => {
    setContractAttachments((current) => [
      ...current,
      {
        id: createAttachmentId(),
        anlageNumber: null,
        label: 'Weitere Anlage',
        filename: '',
        mimeType: '',
        bytes: 0,
        base64: '',
      },
    ]);
  }, []);

  const stateKey = useMemo(
    () => buildContractStateKey(activeContract.key),
    [activeContract.key]
  );

  const legacyStateKey = useMemo(
    () => buildLegacyContractStateKey(activeContract.key, activeVariant.key),
    [activeContract.key, activeVariant.key]
  );

  const originalUrl = `/contracts/${activeVariant.file}`;

  const loadVariantTemplateHtml = useCallback((variant) => ladeVertragsVorlage({
    apiBase,
    authHeaders,
    contractKey: activeContract.key,
    variant: variant?.file ? variant : activeVariant,
    isAuthenticated,
  }), [activeContract.key, activeVariant, apiBase, authHeaders, isAuthenticated]);

  // Lade Vorlage: bevorzugt den im VertragsStudio (Vorlagen > Verträge) gespeicherten Stand,
  // fällt sonst auf das Original-Template zurück. Editor-Panel wird in jedem Fall ausgeblendet.
  const loadTemplate = useCallback(async () => {
    const loadId = templateLoadIdRef.current + 1;
    templateLoadIdRef.current = loadId;
    const isStale = () => templateLoadIdRef.current !== loadId;

    if (isExistingContractEditMode) {
      setLoadingTemplate(false);
      setTemplateHtml('');
      setTemplateSource('uploaded-html');
      return;
    }

    setLoadingTemplate(true);
    setIframeSrc(null);
    setIframeSrcDoc(null);
    setIframeKey((v) => v + 1);
    setPreviewSummary(null);
    setUnresolved([]);
    setFilledHtml(null);
    setHtmlEditMode(false);
    setUploadedHtmlName('');
    setTemplateSource('original');
    setVorlagenFehler('');

    try {
      let html = null;
      let source = 'original';

      // Original immer parallel holen — auch falls saved state da ist (für Auto-Merge fehlender Sections wie Anlage 2)
      const originalPromise = fetch(originalUrl).then((r) => (r.ok ? r.text() : ''));

      if (isAuthenticated) {
        try {
          const stateResp = await axios.get(`${apiBase}/contracts/state`, {
            params: { contract_key: stateKey },
            headers: authHeadersRef.current,
          });
          let state = stateResp.data?.state;
          let stateSource = 'canonical';
          if (!hasStoredContractContent(state) && legacyStateKey !== stateKey) {
            try {
              const legacyResp = await axios.get(`${apiBase}/contracts/state`, {
                params: { contract_key: legacyStateKey },
                headers: authHeadersRef.current,
              });
              const legacyState = legacyResp.data?.state || null;
              if (hasStoredContractContent(legacyState)) {
                state = legacyState;
                stateSource = 'legacy';
              }
            } catch (_legacyError) {
              // Legacy fallback ist nur lesend und optional.
            }
          }
          const sourceHtml = typeof state?.editorState?.sourceHtml === 'string' ? state.editorState.sourceHtml : '';
          if (sourceHtml && stateSource === 'canonical') {
            const baseHtml = await originalPromise;
            if (baseHtml) {
              html = applySourceHtmlToTemplate(baseHtml, sourceHtml);
              source = 'sourceHtml';
            }
          } else if (state?.content) {
            html = state.content;
            source = 'savedContent';
          } else if (sourceHtml) {
            const baseHtml = await originalPromise;
            if (baseHtml) {
              html = applySourceHtmlToTemplate(baseHtml, sourceHtml);
              source = 'syncedFromSibling';
            }
          }
        } catch (err) {
          // Bis 09.10.2026 hier still die Originaldatei - wer das Vorlagen-Recht nicht
          // hatte, erstellte so auf einer alten Fassung. Jetzt: abbrechen und sagen, warum.
          throw standNichtLadbar(err);
        }
      }

      const originalHtml = await originalPromise;
      if (isStale()) return;
      if (!html) {
        if (!originalHtml) throw new Error('Vorlage nicht gefunden.');
        html = originalHtml;
      } else if (originalHtml) {
        // Auto-Merge: fehlende Sections (z.B. Anlage 2) aus aktuellem Original ergänzen, ohne den gespeicherten Stand sonst zu verändern
        html = mergeMissingAnlage2(html, originalHtml);
      }

      html = normalizeDachverpachtungPartyLabels(html, activeContract.key);

      if (isStale()) return;
      setTemplateHtml(html);
      setTemplateSource(source);
      setPlaceholders(extractPlaceholders(html));
      if (!isExistingContractEditMode) {
        const guarded = disableEditorInHtml(html);
        setIframeSrcDoc(injectBaseHref(guarded, '/contracts/'));
      }
    } catch (error) {
      if (isStale()) return;
      console.error(error);
      toast.error(error?.message || 'Vorlage konnte nicht geladen werden.');
      setTemplateHtml('');
      setPlaceholders([]);
      if (error?.standNichtLadbar) {
        setTemplateSource('fehler');
        setVorlagenFehler(error.message);
      }
    } finally {
      if (!isStale()) {
        setLoadingTemplate(false);
      }
    }
  }, [activeContract.key, apiBase, isAuthenticated, isExistingContractEditMode, legacyStateKey, originalUrl, stateKey]);

  // Welcher Vertrag gerade gewählt ist – eine spät eintreffende Antwort für einen
  // vorher gewählten Vertrag darf das Mapping nicht überschreiben.
  const aktuellerMappingSchluessel = useRef(stateKey);
  aktuellerMappingSchluessel.current = stateKey;

  const loadMapping = useCallback(async () => {
    setMappingGeladenFuer(null);
    if (!isAuthenticated) {
      setMappingDraft({});
      setMappingSavedAt(null);
      setMappingGespeichert(null);
      setMappingLadefehler(false);
      return;
    }
    const schluessel = stateKey;
    try {
      const response = await axios.get(CONTRACTS_AM_URL, {
        params: { op: 'mapping', contract_key: schluessel },
        headers: authHeadersRef.current,
      });
      if (aktuellerMappingSchluessel.current !== schluessel) return;
      const mapping = response.data?.mapping;
      setMappingDraft(mapping?.fields || {});
      setMappingSavedAt(mapping?.updatedAt || null);
      setMappingGespeichert(mapping?.fields || {});
      setMappingGeladenFuer(schluessel);
      setMappingLadefehler(false);
    } catch (error) {
      if (aktuellerMappingSchluessel.current !== schluessel) return;
      console.error(error);
      const detail = error?.response?.data?.detail;
      toast.error(`Mapping nicht ladbar${detail ? `: ${detail}` : ''} – Speichern ist gesperrt, bis es geladen ist.`);
      setMappingDraft({});
      setMappingSavedAt(null);
      setMappingGespeichert(null);
      setMappingLadefehler(true);
    }
  }, [isAuthenticated, stateKey]);

  useEffect(() => {
    setFruehereStaendeOffen(false);
    setFruehereStaendeListe(null);
  }, [stateKey]);

  const ladeFruehereStaende = useCallback(async () => {
    if (!isAuthenticated) return;
    const schluessel = stateKey;
    setFruehereStaendeLaden(true);
    try {
      const liste = await axios.get(CONTRACTS_AM_URL, {
        params: { op: 'snapshots', contract_key: schluessel },
        headers: authHeaders,
      });
      const eintraege = Array.isArray(liste.data?.snapshots) ? liste.data.snapshots.slice(0, 10) : [];
      const geladen = [];
      // Nacheinander: höchstens zehn kleine Abrufe, die Ablage soll nicht stoßweise belastet werden.
      for (const eintrag of eintraege) {
        try {
          const antwort = await axios.get(CONTRACTS_AM_URL, {
            params: { op: 'snapshot', contract_key: schluessel, snapshot_id: eintrag.snapshotId },
            headers: authHeaders,
          });
          const stand = antwort.data?.snapshot;
          geladen.push({ id: eintrag.snapshotId, zeit: stand?.uploadedAt || eintrag.uploadedAt, felder: stand?.fields || null });
        } catch (_fehler) {
          geladen.push({ id: eintrag.snapshotId, zeit: eintrag.uploadedAt, felder: null });
        }
      }
      if (aktuellerMappingSchluessel.current !== schluessel) return;
      setFruehereStaendeListe(fruehereStaende(geladen));
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || 'Frühere Stände konnten nicht geladen werden.');
      setFruehereStaendeListe([]);
    } finally {
      setFruehereStaendeLaden(false);
    }
  }, [authHeaders, isAuthenticated, stateKey]);

  const handleToggleFruehereStaende = useCallback(() => {
    const oeffnen = !fruehereStaendeOffen;
    setFruehereStaendeOffen(oeffnen);
    if (oeffnen) ladeFruehereStaende();
  }, [fruehereStaendeOffen, ladeFruehereStaende]);

  const handleStandWiederherstellen = useCallback(async (stand) => {
    const zeit = stand.zeit ? new Date(stand.zeit).toLocaleString('de-DE') : 'unbekannt';
    const umfang = stand.belegt === null ? '' : ` (${stand.belegt} belegte Zuordnungen)`;
    if (!window.confirm(`Mapping auf den Stand zurücksetzen, der bis ${zeit} galt${umfang}? Der jetzige Stand wird vorher gesichert.`)) return;
    setStandWirdWiederhergestellt(stand.id);
    try {
      await axios.post(CONTRACTS_AM_URL, {
        op: 'restore_snapshot',
        contract_key: stateKey,
        snapshot_id: stand.id,
      }, { headers: authHeaders });
      toast.success('Früherer Stand wiederhergestellt.');
      await loadMapping();
      await ladeFruehereStaende();
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || 'Wiederherstellen fehlgeschlagen.');
    } finally {
      setStandWirdWiederhergestellt(null);
    }
  }, [authHeaders, ladeFruehereStaende, loadMapping, stateKey]);

  useEffect(() => { loadTemplate(); }, [loadTemplate]);
  useEffect(() => { loadMapping(); }, [loadMapping]);

  useEffect(() => {
    if (!isAuthenticated) {
      setAvailableClauses([]);
      setSelectedClauseIds([]);
      return undefined;
    }
    let cancelled = false;
    const controller = new AbortController();
    axios.get(`${apiBase}/contracts/state`, {
      params: { resource: 'clauses', contract_type: activeContractKey },
      headers: authHeaders,
      signal: controller.signal,
    }).then((resp) => {
      if (cancelled) return;
      const loaded = Array.isArray(resp.data && resp.data.clauses) ? resp.data.clauses : [];
      loaded.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
      setAvailableClauses(loaded);
      setSelectedClauseIds((prev) => prev.filter((id) => loaded.some((c) => c.id === id)));
    }).catch((error) => {
      if (cancelled || error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') return;
      setAvailableClauses([]);
      setSelectedClauseIds([]);
    });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [apiBase, authHeaders, isAuthenticated, activeContractKey]);

  // Dateien der Anlagen-Felder aus dem Deal. Kurz verzoegert, damit nicht jede
  // getippte Ziffer der Deal-ID eine Abfrage ausloest. Das Token kommt aus dem
  // Ref - eine Token-Erneuerung soll nicht neu laden (Befund 24.10.2026).
  const dealIdFuerAnlagen = /^\d+$/.test(dealId.trim()) ? dealId.trim() : '';
  const anlagenFelderSchluessel = anlagenFelder.join(',');
  useEffect(() => {
    if (!isAuthenticated || !dealIdFuerAnlagen || !anlagenFelderSchluessel) {
      setDealDateien(null);
      setDealDateienFehler('');
      return undefined;
    }
    let abgebrochen = false;
    const controller = new AbortController();
    const zeit = setTimeout(async () => {
      setDealDateienLaden(true);
      setDealDateienFehler('');
      try {
        const antwort = await axios.post(`${apiBase}/contracts/usage`, {
          action: 'deal-anlagen-liste',
          deal_id: dealIdFuerAnlagen,
          felder: anlagenFelderSchluessel.split(','),
        }, { headers: authHeadersRef.current, signal: controller.signal });
        if (abgebrochen) return;
        setDealDateien(antwort.data);
      } catch (error) {
        if (abgebrochen || error?.code === 'ERR_CANCELED') return;
        setDealDateien(null);
        setDealDateienFehler(error?.response?.data?.detail || 'Dateien aus Bitrix ließen sich nicht laden.');
      } finally {
        if (!abgebrochen) setDealDateienLaden(false);
      }
    }, 600);
    return () => {
      abgebrochen = true;
      clearTimeout(zeit);
      controller.abort();
    };
  }, [anlagenFelderSchluessel, apiBase, dealDateienAnstoss, dealIdFuerAnlagen, isAuthenticated]);

  // Aufhebungsvereinbarung: Neuer Deal, neue Angaben. Die Vertragsart schlaegt
  // der Server aus dem Deal vor (wie beim Vorvertrag); das Datum steht in
  // keinem Bitrix-Feld und bleibt leer.
  useEffect(() => {
    setAufhebungAngaben({ ...LEERE_AUFHEBUNG, briefdatum: heuteIso() });
    setAufhebungHinweis('');
    if (!isAuthenticated || !istAufhebungsvertrag || !dealIdFuerAnlagen) return undefined;
    let abgebrochen = false;
    const controller = new AbortController();
    const zeit = setTimeout(async () => {
      try {
        const antwort = await axios.post(`${apiBase}/contracts/usage`, {
          action: 'aufhebung-vorschlag',
          deal_id: dealIdFuerAnlagen,
        }, { headers: authHeadersRef.current, signal: controller.signal });
        if (abgebrochen) return;
        const name = vertragsName(antwort.data?.vertragKey);
        if (name) {
          setAufhebungAngaben((bisher) => (bisher.vertrag ? bisher : { ...bisher, vertrag: name }));
          setAufhebungHinweis('Vertragsart aus dem Deal vorgeschlagen – bitte prüfen. Das Datum steht in keinem Bitrix-Feld.');
        } else {
          setAufhebungHinweis(antwort.data?.grund || 'Die Vertragsart ließ sich aus dem Deal nicht ablesen.');
        }
      } catch (error) {
        if (abgebrochen || error?.code === 'ERR_CANCELED') return;
        setAufhebungHinweis('Vorschlag aus Bitrix ließ sich nicht laden – bitte von Hand wählen.');
      }
    }, 600);
    return () => {
      abgebrochen = true;
      clearTimeout(zeit);
      controller.abort();
    };
  }, [apiBase, dealIdFuerAnlagen, isAuthenticated, istAufhebungsvertrag]);

  // Werkliefervertrag: Neuer Deal, neues Angebot - Datum und Termine des
  // vorigen Vertrags sollen nicht still im naechsten landen. Der Verkaeufer bleibt.
  useEffect(() => {
    setWerkvertragAngaben((bisher) => ({ ...bisher, angebotsdatum: '', eegAbsicherung: false, montagebeginn: '', eegInbetriebnahme: '' }));
  }, [dealIdFuerAnlagen]);

  // Anlage an- oder abwaehlen. Neu gewaehlt und noch ohne Dateiwahl: alle Dateien
  // des Deals vorschlagen (so verhaelt sich auch die Standardauswahl).
  const handleAnlageUmschalten = useCallback((bausteinId) => {
    const istAn = selectedClauseIds.includes(bausteinId);
    setSelectedClauseIds((bisher) => (istAn ? bisher.filter((id) => id !== bausteinId) : [...bisher, bausteinId]));
    if (istAn || anlagenAuswahl[bausteinId] || !dealDateien) return;
    const baustein = anlagenBausteine.find((b) => b.id === bausteinId);
    const vorschlag = standardAuswahl(baustein ? [{ ...baustein, standard: true }] : [], dealDateien).auswahl[bausteinId];
    if (vorschlag) setAnlagenAuswahl((bisher) => ({ ...bisher, [bausteinId]: vorschlag }));
  }, [anlagenAuswahl, anlagenBausteine, dealDateien, selectedClauseIds]);

  // Standardauswahl einmal je Deal und Vertrag, sobald die Dateien da sind.
  useEffect(() => {
    if (!dealDateien || !dealIdFuerAnlagen) return;
    const schluessel = `${activeContractKey}:${dealIdFuerAnlagen}`;
    if (standardAngewandtFuer.current === schluessel) return;
    standardAngewandtFuer.current = schluessel;
    const { ids, auswahl } = standardAuswahl(anlagenBausteine, dealDateien);
    setAnlagenAuswahl(auswahl);
    setSelectedClauseIds((bisher) => [...new Set([...bisher, ...ids])]);
  }, [activeContractKey, anlagenBausteine, dealDateien, dealIdFuerAnlagen]);

  const handleMappingChange = useCallback((placeholder, value) => {
    setMappingDraft((current) => ({ ...current, [placeholder]: value }));
  }, []);

  // Vorschläge aus den Bitrix-Feldern für leere Felder (seit 05.10.2026).
  const mappingVorschlag = useMemo(
    () => mappingMitVorschlaegen(mappingDraft, automatedMappingPlaceholders, mappingVorschlaegeFuer(activeContract.key)),
    [activeContract.key, automatedMappingPlaceholders, mappingDraft]
  );

  const handleVorschlaegeEinsetzen = useCallback(() => {
    const { felder, eingesetzt } = mappingMitVorschlaegen(mappingDraft, automatedMappingPlaceholders, mappingVorschlaegeFuer(activeContract.key));
    if (!eingesetzt.length) return;
    setMappingDraft(felder);
    toast.success(`${eingesetzt.length} Felder vorgeschlagen – bitte prüfen und dann „Speichern“.`);
  }, [activeContract.key, automatedMappingPlaceholders, mappingDraft]);

  const handleRemoveStaleField = useCallback((placeholder) => {
    setMappingDraft((current) => {
      if (!Object.prototype.hasOwnProperty.call(current, placeholder)) return current;
      const next = { ...current };
      delete next[placeholder];
      return next;
    });
  }, []);

  // Eine Liste für alles (seit 05.10.2026): Gespeicherte Zuordnungen, die in der
  // geladenen Vorlage fehlen, bleiben als Zeile stehen, bis jemand ✕ drückt.
  const mappingListe = useMemo(() => mappingZeilen({
    zuzuordnen: automatedMappingPlaceholders,
    felder: mappingDraft,
    lageplanVerfuegbar: currentLageplanInfo.available,
  }), [automatedMappingPlaceholders, mappingDraft, currentLageplanInfo.available]);
  const nichtInVorlage = useMemo(
    () => mappingListe.filter((zeile) => !zeile.inVorlage).length,
    [mappingListe]
  );

  const handleSaveMapping = useCallback(async () => {
    if (!isAuthenticated) {
      toast.error('Bitte zuerst einloggen.');
      return;
    }
    if (mappingLadefehler || mappingGeladenFuer !== stateKey) {
      toast.error('Das gespeicherte Mapping ist nicht geladen – Speichern würde es überschreiben. Bitte erst neu laden.');
      return;
    }
    const felder = removeBuyerFieldsFromMapping(mappingDraft, isBuyerPartnerFlow);
    const warnung = mappingSpeichernWarnung(mappingGespeichert, felder);
    if (warnung && !window.confirm(warnung)) return;
    setSaving(true);
    try {
      await axios.post(CONTRACTS_AM_URL, {
        op: 'mapping',
        contract_key: stateKey,
        fields: felder,
      }, { headers: authHeaders });
      setMappingSavedAt(new Date().toISOString());
      setMappingGespeichert(felder);
      toast.success('Mapping gespeichert.');
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || error?.message || 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  }, [authHeaders, isAuthenticated, isBuyerPartnerFlow, mappingDraft, mappingGeladenFuer, mappingGespeichert, mappingLadefehler, stateKey]);

  const handleResetPreview = useCallback(() => {
    if (templateHtml && !isExistingContractEditMode) {
      const guarded = disableEditorInHtml(templateHtml);
      setIframeSrcDoc(injectBaseHref(guarded, '/contracts/'));
      setIframeSrc(null);
      setIframeKey((v) => v + 1);
    } else if (isExistingContractEditMode) {
      setIframeSrcDoc(null);
      setIframeSrc(null);
      setIframeKey((v) => v + 1);
    }
    setPreviewSummary(null);
    setUnresolved([]);
    setFilledHtml(null);
    setHtmlEditMode(false);
    setUploadedHtmlName('');
    setUploadedContractInfo(null);
    setContractAttachments([]);
    setUploadSiblingVariantForHtml(true);
  }, [isExistingContractEditMode, templateHtml]);

  // Wirft, solange der Vertrag im Editor noch eingelesen wird (leseKundenvertragAusRahmen) -
  // ein Export aus dem halb geladenen iframe enthielt nur das Deckblatt.
  const readCurrentContractHtml = useCallback(() => {
    if (!htmlEditMode) {
      return filledHtml || '';
    }

    const html = leseKundenvertragAusRahmen(iframeRef.current);
    setFilledHtml(html);
    return html;
  }, [filledHtml, htmlEditMode]);

  // `seitenvorlage`: Ersatz, wenn die Datei keine Seitenvorlage hat und sich keine
  // nachbauen lässt (siehe ladeErsatzSeitenvorlage).
  const openHtmlInCustomerEditor = useCallback((html, name = '', contractOverride = null) => {
    templateLoadIdRef.current += 1;
    const editorContract = getContractDefinition(contractOverride?.contractKey) || activeContract;
    const editorVariant = getVariantDefinition(editorContract, contractOverride?.variantKey) || activeVariant;
    const editableHtml = prepareCustomerEditableHtml(html, {
      contractKey: editorContract.key,
      variantKey: editorVariant.key,
      seitenvorlage: contractOverride?.seitenvorlage || '',
    });
    setIframeSrc(null);
    setIframeSrcDoc(injectBaseHref(editableHtml, '/contracts/'));
    setIframeKey((value) => value + 1);
    setFilledHtml(html);
    setHtmlEditMode(true);
    setUploadedHtmlName(name);
    setPlaceholders(extractPlaceholders(html));
    setPreviewSummary(null);
    setUnresolved([]);
  }, [activeContract, activeVariant]);

  // Seitenvorlage der Originaldatei für eine Datei, die weder eine Seitenvorlage noch
  // eine gerenderte Seite hat - etwa ein Entwurf, der vor dem 25.10.2026 aus einer mit
  // „Seite speichern unter“ gesicherten Datei entstand. Leer, wenn nichts nötig ist
  // oder das Laden scheitert.
  const ladeErsatzSeitenvorlage = useCallback(async (html, variant) => {
    if (!seitenvorlageFehltGanz(html) || !variant?.file) return '';
    try {
      const antwort = await fetch(`/contracts/${variant.file}`);
      return antwort.ok ? seitenvorlageAus(await antwort.text()) : '';
    } catch (_error) {
      return '';
    }
  }, []);

  // Hochgeladene Datei oder wiederhergestellten Entwurf in den Editor: Vertragsart
  // erkennen (oder aus dem Entwurf übernehmen), Seitenvorlage sichern, öffnen.
  const oeffneVertragsDatei = useCallback(async (html, name, vorgabe = null) => {
    const vorgegeben = getContractDefinition(vorgabe?.contractKey);
    const detected = vorgegeben
      ? { contract: vorgegeben, variant: getVariantDefinition(vorgegeben, vorgabe?.variantKey), reason: 'content' }
      : detectContractFromHtml(html, name, CONTRACTS);
    const contract = detected?.contract || null;
    const variant = contract ? (detected.variant || contract.variants[0]) : null;
    // Nur mit erkannter Vertragsart - sonst bekäme der Vertrag Kopf und Fuß eines anderen.
    const seitenvorlage = await ladeErsatzSeitenvorlage(html, variant);
    setUploadSiblingVariantForHtml(true);
    if (contract) {
      setActiveContractKey(contract.key);
      setActiveVariantKey(variant?.key || 'ci');
      setUploadedContractInfo({
        contractKey: contract.key,
        contractLabel: contract.label,
        variantKey: variant?.key || 'ci',
        variantLabel: variant?.label || 'CI',
        reason: detected.reason || 'content',
      });
    } else {
      setUploadedContractInfo(null);
    }
    openHtmlInCustomerEditor(html, name, contract
      ? { contractKey: contract.key, variantKey: variant?.key || 'ci', seitenvorlage }
      : { seitenvorlage });
    if (seitenvorlageFehltGanz(html) && !seitenvorlage) {
      toast.warning('In dieser Datei fehlt die Seitenvorlage – der Vertrag lässt sich nicht in Seiten setzen. Bitte die HTML-Datei des Vertrags erneut aus dem Tool herunterladen.', { duration: 15000 });
    }
    return contract;
  }, [ladeErsatzSeitenvorlage, openHtmlInCustomerEditor]);

  const handleImportHtmlFile = useCallback(async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file) return;
    if (!/\.html?$/i.test(file.name) && file.type && !/html/i.test(file.type)) {
      toast.error('Bitte eine HTML-Datei auswählen.');
      return;
    }

    try {
      const html = await file.text();
      const contract = await oeffneVertragsDatei(html, file.name);
      if (contract) {
        toast.success(`HTML-Datei geöffnet. Erkannt: ${contract.label}.`);
      } else {
        toast.success('HTML-Datei im Kundenvertrag-Editor geöffnet. Vertragsart bitte oben prüfen.');
      }
    } catch (error) {
      console.error(error);
      toast.error(error?.message || 'HTML-Datei konnte nicht geladen werden.');
    }
  }, [oeffneVertragsDatei]);

  useEffect(() => {
    // Beim Start pruefen, ob ein ungesicherter Entwurf vorliegt. Ohne Vertragsquelle
    // ist er nicht zu gebrauchen (etwa ein Export aus dem halb geladenen Editor).
    try {
      const roh = localStorage.getItem(ENTWURF_KEY);
      if (!roh) return;
      const alt = JSON.parse(roh);
      if (alt && typeof alt.html === 'string' && alt.html.length > 200 && alt.html.includes('contract-source')) setEntwurf(alt);
      else localStorage.removeItem(ENTWURF_KEY);
    } catch (_e) { /* Entwurf ist optional */ }
  }, []);

  // Entwurfssicherung (umgebaut am 25.10.2026). Vorher hing der Effekt an filledHtml
  // und readCurrentContractHtml, und sein Aufräumen rief readCurrentContractHtml auf -
  // das setzt filledHtml. Weil der Editor bei jedem Export einen neuen Zeitstempel
  // (savedAt) in den Vertrag schreibt, war jeder Export anders: Aufräumen → Export →
  // neuer State → Aufräumen … Gemessen mit einem 5,5-MB-Vertrag: ab dem ersten
  // 20-Sekunden-Takt gut vier Exporte samt 1,3 MB localStorage je Sekunde, ohne Ende.
  // Außerdem exportierte das Aufräumen nach jedem Neuladen des iframes (neuer key) das
  // gerade erst beginnende Dokument. Jetzt: kein State, die Werte über eine Ref,
  // gesichert im Takt, beim Verlassen der Seite und beim Abbau - und nur aus einem
  // fertig eingelesenen Editor.
  const entwurfsStandRef = useRef(null);
  entwurfsStandRef.current = {
    htmlEditMode,
    filledHtml,
    dealId,
    name: uploadedHtmlName || '',
    contractKey: activeContract?.key || '',
    contractLabel: activeContract?.label || '',
    variantKey: activeVariant?.key || '',
  };
  const entwurfAktiv = htmlEditMode || Boolean(filledHtml);

  useEffect(() => {
    if (!entwurfAktiv) return undefined;
    const sichern = () => {
      const { htmlEditMode: imEditor, filledHtml: gefuellt, ...angaben } = entwurfsStandRef.current;
      let html = gefuellt;
      if (imEditor) {
        // Beim Abbau ist das iframe schon weg - dann gilt der zuletzt gesicherte
        // Stand; filledHtml wäre hier die unbearbeitete Datei.
        const iframe = iframeRef.current;
        if (!iframe || !editorBereit(iframe)) return;
        try { html = leseKundenvertragAusRahmen(iframe); } catch (_e) { return; }
      }
      if (typeof html !== 'string' || html.length < 200 || !html.includes('contract-source')) return;
      // localStorage fasst etwa 5 MB; ein zu großer Entwurf würde nur scheitern.
      if (html.length > ENTWURF_HOECHSTENS_ZEICHEN) return;
      try {
        localStorage.setItem(ENTWURF_KEY, JSON.stringify({ html, ...angaben, ts: new Date().toISOString() }));
      } catch (_e) { /* Speicher voll o. ae. - Sicherung ist optional */ }
    };
    const takt = window.setInterval(sichern, 20000);
    window.addEventListener('beforeunload', sichern);
    return () => {
      window.clearInterval(takt);
      window.removeEventListener('beforeunload', sichern);
      sichern();
    };
  }, [entwurfAktiv]);

  const applyHtmlToCurrentContractView = useCallback((html, name = uploadedHtmlName || '') => {
    if (htmlEditMode || isExistingContractEditMode) {
      openHtmlInCustomerEditor(html, name);
    } else {
      setIframeSrc(null);
      setFilledHtml(html);
      setIframeSrcDoc(injectBaseHref(disableEditorInHtml(html), '/contracts/'));
      setIframeKey((value) => value + 1);
      setPlaceholders(extractPlaceholders(html));
    }
  }, [htmlEditMode, isExistingContractEditMode, openHtmlInCustomerEditor, uploadedHtmlName]);

  // Beim Bearbeiten wie ein Hochladen: mit Vertragsart und Seitenvorlage. Bis
  // 25.10.2026 blieb die Startauswahl „Kaufvertrag Kleinspeicher“ stehen.
  const handleEntwurfWiederherstellen = useCallback(async () => {
    const alt = entwurf;
    if (!alt) return;
    setEntwurf(null);
    if (alt.dealId) setDealId(alt.dealId);
    if (!isExistingContractEditMode) {
      applyHtmlToCurrentContractView(alt.html, alt.name || '');
      return;
    }
    try {
      await oeffneVertragsDatei(alt.html, alt.name || '', alt);
    } catch (error) {
      console.error(error);
      toast.error(error?.message || 'Der Entwurf konnte nicht geöffnet werden.');
    }
  }, [applyHtmlToCurrentContractView, entwurf, isExistingContractEditMode, oeffneVertragsDatei]);

  const handleRefreshMappedFieldsFromDeal = useCallback(async () => {
    if (!isAuthenticated) {
      toast.error('Bitte zuerst einloggen.');
      return;
    }
    if (!dealId.trim()) {
      toast.error('Bitte eine Bitrix-Deal-ID eingeben.');
      return;
    }
    if (!filledHtml && !htmlEditMode) {
      toast.error('Bitte zuerst eine HTML-Vertragsdatei laden.');
      return;
    }

    setRefreshingMappedFields(true);
    try {
      const currentHtml = applyBuyerFieldsToAutomationHtml(htmlEditMode ? readCurrentContractHtml() : filledHtml);
      const loaded = await loadVariantTemplateHtml(activeVariant);
      const templateForRefresh = applyBuyerFieldsToAutomationHtml(loaded.html);
      if (!extractPlaceholders(templateForRefresh).length) {
        throw new Error('In der Vertragsvorlage wurden keine Mapping-Platzhalter gefunden.');
      }

      const response = await axios.post(`${apiBase}/contracts/usage`, {
        action: 'am-contract-preview',
        contract_key: stateKey,
        contract_label: activeContract.label,
        template_html: templateForRefresh,
        deal_id: dealId.trim(),
        template_variant: activeVariant.key,
        variant_label: activeVariant.label,
        mapping: { fields: mappingFieldsForGeneration },
        clauses: [],
        additionalOwners: additionalOwnersPayload,
      }, { headers: authHeaders });

      const generatedHtml = response.data?.html;
      if (typeof generatedHtml !== 'string' || !generatedHtml) {
        throw new Error('Backend lieferte kein HTML.');
      }

      const merged = refreshMappedFieldsInEditedHtml(currentHtml, templateForRefresh, generatedHtml);
      merged.html = applyBuyerFieldsToAutomationHtml(merged.html);
      applyHtmlToCurrentContractView(merged.html, uploadedHtmlName || '');
      setPreviewSummary(response.data?.bitrixSummary || null);
      setUnresolved(Array.isArray(response.data?.unresolved) ? response.data.unresolved : []);
      if (merged.updated > 0) {
        toast.success(`Deal-Daten aktualisiert (${merged.updated} Stellen).`);
      } else if (merged.unchanged > 0 && merged.skipped === 0) {
        toast.success('Deal-Daten sind bereits aktuell.');
      } else {
        toast.warning('Es wurden keine Mapping-Stellen aktualisiert. Bitte Vertragsart, Variante und Mapping prüfen.');
      }
      if (merged.skipped > 0) {
        toast.info(`${merged.skipped} Stellen konnten nicht sicher zugeordnet werden und wurden nicht verändert.`);
      }
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || error?.message || 'Deal-Daten konnten nicht aktualisiert werden.');
    } finally {
      setRefreshingMappedFields(false);
    }
  }, [activeContract.label, activeVariant, additionalOwnersPayload, apiBase, applyBuyerFieldsToAutomationHtml, applyHtmlToCurrentContractView, authHeaders, dealId, filledHtml, htmlEditMode, isAuthenticated, loadVariantTemplateHtml, mappingFieldsForGeneration, readCurrentContractHtml, stateKey, uploadedHtmlName]);

  const handleReplaceLageplanFromFile = useCallback(async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file) return;
    if (!/^image\//i.test(file.type || '') && !/\.(png|jpe?g|webp|gif|svg)$/i.test(file.name || '')) {
      toast.error('Bitte eine Bilddatei ausw\u00e4hlen.');
      return;
    }

    try {
      const currentHtml = applyBuyerFieldsToAutomationHtml(htmlEditMode ? readCurrentContractHtml() : (filledHtml || templateHtml));
      const dataUrl = await readImageFileAsDataUrl(file);
      const nextHtml = replaceLageplanImageSource(currentHtml, dataUrl, { fileName: file.name });
      applyHtmlToCurrentContractView(nextHtml, uploadedHtmlName || '');
      toast.success('Lageplan-Bild wurde ersetzt.');
    } catch (error) {
      console.error(error);
      toast.error(error?.message || 'Lageplan-Bild konnte nicht ersetzt werden.');
    }
  }, [applyBuyerFieldsToAutomationHtml, applyHtmlToCurrentContractView, filledHtml, htmlEditMode, readCurrentContractHtml, templateHtml, uploadedHtmlName]);

  const handleApplyLageplanMapping = useCallback(async () => {
    if (!isAuthenticated) {
      toast.error('Bitte zuerst einloggen.');
      return;
    }
    if (!dealId.trim()) {
      toast.error('Bitte eine Bitrix-Deal-ID eingeben.');
      return;
    }
    const lageplanMapping = String(mappingDraft[LAGEPLAN_PLACEHOLDER] || '').trim();
    if (!lageplanMapping) {
      toast.error('Bitte zuerst das Bitrix-Feld f\u00fcr Lageplan_Bild eintragen.');
      return;
    }

    setLageplanApplying(true);
    try {
      const currentHtml = applyBuyerFieldsToAutomationHtml(htmlEditMode ? readCurrentContractHtml() : (filledHtml || templateHtml));
      const placeholderHtml = replaceLageplanImageSource(currentHtml, LAGEPLAN_PLACEHOLDER_TOKEN, { dynamic: true });
      const response = await axios.post(`${apiBase}/contracts/usage`, {
        action: 'am-contract-preview',
        contract_key: stateKey,
        contract_label: activeContract.label,
        template_html: placeholderHtml,
        deal_id: dealId.trim(),
        template_variant: activeVariant.key,
        variant_label: activeVariant.label,
        mapping: {
          fields: {
            ...mappingFieldsForGeneration,
            [LAGEPLAN_PLACEHOLDER]: lageplanMapping,
          },
        },
        clauses: [],
        additionalOwners: additionalOwnersPayload,
      }, { headers: authHeaders });

      const nextHtml = response.data?.html;
      if (typeof nextHtml !== 'string' || !nextHtml) {
        throw new Error('Backend lieferte kein HTML.');
      }
      // Beim Bearbeiten geht hier noch der ganze Vertrag durch contracts-am - ein
      // gekürztes Ergebnis ersetzt ihn nicht (seit 25.10.2026, wie beim Einfügen).
      const fehlt = htmlEditMode ? vertragUnvollstaendig(currentHtml, nextHtml) : '';
      if (fehlt) throw new Error(`Lageplan nicht übernommen. ${fehlt}`);
      applyHtmlToCurrentContractView(nextHtml, uploadedHtmlName || '');
      setPreviewSummary(response.data?.bitrixSummary || null);
      setUnresolved(Array.isArray(response.data?.unresolved) ? response.data.unresolved : []);
      const nextInfo = getLageplanImageInfo(nextHtml);
      if (nextInfo.isPlaceholder || !nextInfo.hasImage) {
        toast.warning('Lageplan-Mapping wurde ausgef\u00fchrt, aber kein Bildwert erkannt. Bitte Feldcode pr\u00fcfen.');
      } else {
        toast.success('Lageplan wurde aus Bitrix neu \u00fcbernommen.');
      }
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || error?.message || 'Lageplan konnte nicht aus Bitrix \u00fcbernommen werden.');
    } finally {
      setLageplanApplying(false);
    }
  }, [activeContract.label, activeVariant.key, activeVariant.label, additionalOwnersPayload, apiBase, applyBuyerFieldsToAutomationHtml, applyHtmlToCurrentContractView, authHeaders, dealId, filledHtml, htmlEditMode, isAuthenticated, mappingDraft, mappingFieldsForGeneration, readCurrentContractHtml, stateKey, templateHtml, uploadedHtmlName]);

  const handleEditFilledContract = useCallback(() => {
    if (!filledHtml) {
      toast.error('Bitte erst eine Vorschau füllen oder eine HTML-Datei laden.');
      return;
    }

    try {
      openHtmlInCustomerEditor(applyBuyerFieldsToAutomationHtml(filledHtml), uploadedHtmlName || '');
      toast.success('Vertrag ist jetzt individuell bearbeitbar.');
    } catch (error) {
      console.error(error);
      toast.error(error?.message || 'Editor konnte nicht gestartet werden.');
    }
  }, [applyBuyerFieldsToAutomationHtml, filledHtml, openHtmlInCustomerEditor, uploadedHtmlName]);

  const handleApplyClausesToEditedHtml = useCallback(async () => {
    if (!htmlEditMode) {
      toast.error('Bitte zuerst eine HTML-Datei laden oder den Vertrag individuell bearbeiten.');
      return;
    }
    if (!dealId.trim()) {
      toast.error('Bitte eine Bitrix-Deal-ID eingeben, damit dynamische Felder in Passus befüllt werden können.');
      return;
    }
    if (selectedClauses.length === 0) {
      toast.error('Bitte mindestens einen optionalen Passus auswählen.');
      return;
    }

    setPreviewing(true);
    try {
      const html = applyBuyerFieldsToAutomationHtml(readCurrentContractHtml());
      if (!html) {
        throw new Error('Kein bearbeiteter HTML-Inhalt vorhanden.');
      }

      // Seit 25.10.2026 eigener Weg (api/_lib/klauselnEinfuegen.js): Bis dahin ging
      // der ganze bearbeitete Vertrag als Vorlage durch am-contract-preview und
      // contracts-am - nach dem Einfügen stand nur noch das Deckblatt da. Jetzt
      // fügt der Server nur ein; Platzhalter füllt er allein im jeweiligen Passus.
      const response = await axios.post(`${apiBase}/contracts/usage`, {
        action: 'am-klauseln-einfuegen',
        contract_key: stateKey,
        contract_label: activeContract.label,
        template_html: html,
        deal_id: dealId.trim(),
        template_variant: activeVariant.key,
        variant_label: activeVariant.label,
        mapping: { fields: mappingFieldsForGeneration },
        clauses: selectedClauses,
      }, { headers: authHeaders });

      const nextHtml = response.data?.html;
      if (typeof nextHtml !== 'string' || !nextHtml) {
        throw new Error('Backend lieferte kein HTML.');
      }
      const fehlt = vertragUnvollstaendig(html, nextHtml);
      if (fehlt) throw new Error(`Einfügen abgebrochen. ${fehlt}`);

      openHtmlInCustomerEditor(nextHtml, uploadedHtmlName || '');
      setSelectedClauseIds([]);
      setUnresolved(Array.isArray(response.data?.unresolved) ? response.data.unresolved : []);
      toast.success('Ausgewählte Passus wurden in den bearbeiteten Vertrag eingefügt.');
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || error?.message || 'Passus konnten nicht eingefügt werden.');
    } finally {
      setPreviewing(false);
    }
  }, [activeContract.label, activeVariant.key, activeVariant.label, apiBase, applyBuyerFieldsToAutomationHtml, authHeaders, dealId, htmlEditMode, mappingFieldsForGeneration, openHtmlInCustomerEditor, readCurrentContractHtml, selectedClauses, stateKey, uploadedHtmlName]);

  // Der Vertrag, den das Gespraech gerade sieht. Beim Bearbeiten liest das
  // den Editor aus - sonst saehe die KI den Stand vom Hochladen, nicht die
  // Aenderungen, die der Bearbeiter seitdem von Hand gemacht hat.
  const holeGespraechsHtml = useCallback(() => {
    if (!htmlEditMode) return gespraech.html;
    try {
      return applyBuyerFieldsToAutomationHtml(readCurrentContractHtml());
    } catch (_error) {
      return gespraech.html;
    }
  }, [applyBuyerFieldsToAutomationHtml, gespraech.html, htmlEditMode, readCurrentContractHtml]);

  // „… in den Vertrag übernehmen“ gibt es nur noch beim Bearbeiten: Die
  // Ersetzungen gehen in den Vertrag im Editor. saveContractState() wird nicht
  // angefasst - die geteilte Vorlage bleibt, wie sie ist.
  //
  // Beim Erstellen setzte der Knopf bis 24.10.2026 das Ergebnis als Vorlage der
  // aktiven Variante ein. Die Vorschau zeigte es nicht (sie blieb beim alten
  // Stand), nach dem Füllen wurde der GEFÜLLTE Vertrag zur Vorlage, die
  // Druckversion ging ohne die Ersetzung nach Bitrix, und ein Variantenwechsel
  // warf sie still hinaus. Jetzt mitGespraechsErsetzungen beim Füllen und
  // Abschließen.
  const uebernimmGespraechsHtml = useCallback((html) => {
    applyHtmlToCurrentContractView(html, uploadedHtmlName || '');
  }, [applyHtmlToCurrentContractView, uploadedHtmlName]);

  // Beim Erstellen: Vorlage jeder Variante + zugestimmte Ersetzungen. Ohne
  // Ersetzungen (oder beim Bearbeiten) kommen die Varianten unverändert zurück.
  // `fehler` nennt Ersetzungen, die in einer Variante nicht greifen - das
  // Füllen warnt, das Abschließen bricht ab.
  const ersetzungenBeimErstellen = gespraech.ersetzungenIn === 'vorlage' && gespraechsErsetzungen.length > 0;
  const mitGespraechsErsetzungen = useCallback(async (varianten) => {
    if (!ersetzungenBeimErstellen || !dealId.trim()) return { varianten, fehler: '' };
    let ergebnisse;
    try {
      ergebnisse = await ersetzungenInVarianten({
        apiBase, authHeaders, dealId: dealId.trim(), contractKey: activeContract.key, varianten,
      });
    } catch (fehler) {
      const grund = fehler?.response?.data?.detail || fehler?.message || 'unbekannter Fehler';
      throw new Error(`Zugestimmte Ersetzungen aus dem Gespräch ließen sich nicht einsetzen: ${grund}`);
    }
    return { varianten: ergebnisse, fehler: ersetzungsFehlerText(ergebnisse) };
  }, [activeContract.key, apiBase, authHeaders, dealId, ersetzungenBeimErstellen]);

  // Was beim Füllen aus dem Gespräch mitkommt: freigegebene Ergänzungen und
  // (beim Erstellen) zugestimmte Ersetzungen. Ändert es sich, nachdem gefüllt
  // ist, füllt die Vorschau neu (Effekt unten) - eine Entscheidung im Gespräch
  // steht so gleich im Vertrag, ohne eigenen Knopf (seit 24.10.2026).
  const gespraechsStand = useMemo(() => JSON.stringify({
    klauseln: verhandlungsKlauseln,
    ersetzungen: gespraech.ersetzungenIn === 'vorlage'
      ? gespraechsErsetzungen.map((a) => [a.id, a.art, a.fundstelle, a.neu])
      : [],
  }), [gespraech.ersetzungenIn, gespraechsErsetzungen, verhandlungsKlauseln]);
  const gespraechsStandRef = useRef(gespraechsStand);
  gespraechsStandRef.current = gespraechsStand;
  // Mit welchem Stand und für welchen Deal/Vertrag zuletzt gefüllt wurde, und
  // welcher Stand zuletzt von selbst versucht wurde (kein zweiter Versuch, wenn
  // das Füllen scheitert).
  const gefuelltMit = useRef(null);
  const selbstVersucht = useRef('');

  const handleDownloadHtml = useCallback(async () => {
    if (!filledHtml) {
      toast.error('Erst die Vorschau füllen, dann steht der Download bereit.');
      return;
    }
    try {
      const safeName = `${activeContract.label}_${activeVariant.label}_Deal-${(dealId || 'preview').replace(/[^A-Za-z0-9_-]+/g, '')}.html`;
      // Eigenständig, sonst zeigt die Datei vom Rechner nur das Deckblatt (16.10.2026).
      const { html, vollstaendig } = await machVertragsHtmlEigenstaendig(filledHtml);
      const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = safeName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(link.href), 1000);
      if (vollstaendig) toast.success('HTML heruntergeladen.');
      else toast.warning(`HTML heruntergeladen. ${NUR_ONLINE_HINWEIS}`);
    } catch (error) {
      console.error(error);
      toast.error('Download fehlgeschlagen.');
    }
  }, [filledHtml, activeContract.label, activeVariant.label, dealId]);

  const handlePrintPdf = useCallback(() => {
    if (!filledHtml) {
      toast.error('Erst die Vorschau füllen, dann ist Drucken/PDF möglich.');
      return;
    }
    const iframe = iframeRef.current;
    if (!iframe || !iframe.contentWindow) {
      toast.error('Vorschau nicht verfügbar.');
      return;
    }
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } catch (error) {
      console.error(error);
      toast.error('Drucken fehlgeschlagen.');
    }
  }, [filledHtml]);

  const handleDownloadCurrentHtml = useCallback(async () => {
    if (!filledHtml) {
      toast.error('Erst die Vorschau füllen oder eine HTML-Datei laden.');
      return;
    }
    try {
      const safeName = `${activeContract.label}_${activeVariant.label}_Deal-${(dealId || 'preview').replace(/[^A-Za-z0-9_-]+/g, '')}.html`;
      const { html, vollstaendig } = await machVertragsHtmlEigenstaendig(
        applyBuyerFieldsToAutomationHtml(readCurrentContractHtml()),
      );
      const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = safeName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(link.href), 1000);
      if (vollstaendig) toast.success('HTML heruntergeladen.');
      else toast.warning(`HTML heruntergeladen. ${NUR_ONLINE_HINWEIS}`);
    } catch (error) {
      console.error(error);
      toast.error(error?.message === EDITOR_LAEDT_NOCH ? EDITOR_LAEDT_NOCH : 'Download fehlgeschlagen.');
    }
  }, [applyBuyerFieldsToAutomationHtml, filledHtml, readCurrentContractHtml, activeContract.label, activeVariant.label, dealId]);

  const handlePrintCurrentPdf = useCallback(() => {
    if (!filledHtml) {
      toast.error('Erst die Vorschau füllen oder eine HTML-Datei laden.');
      return;
    }
    const iframe = iframeRef.current;
    if (!iframe || !iframe.contentWindow) {
      toast.error('Vorschau nicht verf?gbar.');
      return;
    }
    try {
      if (htmlEditMode) readCurrentContractHtml();
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } catch (error) {
      console.error(error);
      toast.error(error?.message === EDITOR_LAEDT_NOCH ? EDITOR_LAEDT_NOCH : 'Drucken fehlgeschlagen.');
    }
  }, [filledHtml, htmlEditMode, readCurrentContractHtml]);

  // `{ vonSelbst: true }`: neu gefüllt, weil sich im Gespräch etwas entschieden hat.
  const handleRunPreview = useCallback(async (optionen) => {
    const vonSelbst = optionen?.vonSelbst === true;
    if (!isAuthenticated) {
      toast.error('Bitte zuerst einloggen.');
      return;
    }
    if (!dealId.trim()) {
      toast.error('Bitte eine Bitrix-Deal-ID eingeben.');
      return;
    }
    if (!templateHtml) {
      toast.error('Vorlage noch nicht geladen.');
      return;
    }
    setPreviewing(true);
    const stand = gespraechsStandRef.current;
    // Offene Lücken („[Frist Baubeginn]“) zeigt die Vorschau - Abschließen sperrt sie.
    const offeneLuecken = offeneGespraechsLuecken({ ersetzungen: gespraechsErsetzungen, klauseln: verhandlungsKlauseln });
    if (offeneLuecken.length && !vonSelbst) toast.warning(lueckenSperrText(offeneLuecken), { duration: 12000 });
    try {
      // Zugestimmte Ersetzungen: in die aktive Variante zum Füllen, in die
      // anderen nur zur Probe - greift eine dort nicht, soll man es jetzt
      // erfahren und nicht erst beim Abschließen. Lädt eine andere Variante
      // nicht, prüft das Abschließen sie.
      let vorlage = templateHtml;
      if (ersetzungenBeimErstellen) {
        const andere = [];
        for (const variant of activeContract.variants.filter((v) => v.key !== activeVariant.key)) {
          try {
            const geladen = await loadVariantTemplateHtml(variant);
            if (geladen?.html) andere.push({ key: variant.key, label: variant.label, html: geladen.html });
          } catch (_fehler) { /* siehe oben */ }
        }
        const { varianten, fehler } = await mitGespraechsErsetzungen([
          { key: activeVariant.key, label: activeVariant.label, html: templateHtml },
          ...andere,
        ]);
        vorlage = varianten[0].html;
        if (fehler) toast.warning(fehler, { duration: 20000 });
      }
      const response = await axios.post(`${apiBase}/contracts/usage`, {
        action: 'am-contract-preview',
        contract_key: stateKey,
        contract_label: activeContract.label,
        template_html: applyBuyerFieldsToAutomationHtml(vorlage),
        deal_id: dealId.trim(),
        template_variant: activeVariant.key,
        variant_label: activeVariant.label,
        mapping: { fields: mappingFieldsForGeneration },
        clauses: selectedClauses,
        additionalOwners: additionalOwnersPayload,
      }, { headers: authHeaders });
      const html = response.data?.html;
      if (typeof html !== 'string' || !html) throw new Error('Backend lieferte kein HTML.');
      setIframeSrc(null);
      setFilledHtml(html);
      setHtmlEditMode(false);
      setUploadedHtmlName('');
      setIframeSrcDoc(injectBaseHref(disableEditorInHtml(html), '/contracts/'));
      setIframeKey((v) => v + 1);
      setPreviewSummary(response.data?.bitrixSummary || null);
      setUnresolved(Array.isArray(response.data?.unresolved) ? response.data.unresolved : []);
      gefuelltMit.current = { stand, deal: dealId.trim(), vertrag: stateKey, variante: activeVariant.key };
      toast.success(vonSelbst ? 'Vorschau neu gefüllt – mit deiner Entscheidung aus dem Gespräch.' : 'Vorschau gefüllt.');
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || error?.message || 'Vorschau fehlgeschlagen.');
    } finally {
      setPreviewing(false);
    }
  }, [activeContract.label, activeContract.variants, activeVariant.key, activeVariant.label, additionalOwnersPayload, apiBase, applyBuyerFieldsToAutomationHtml, authHeaders, ersetzungenBeimErstellen, gespraechsErsetzungen, isAuthenticated, dealId, loadVariantTemplateHtml, mitGespraechsErsetzungen, stateKey, templateHtml, mappingFieldsForGeneration, selectedClauses, verhandlungsKlauseln]);

  // Nach einer Entscheidung im Gespräch (Freigeben, Zustimmen, Ablehnen) die
  // schon gefüllte Vorschau neu füllen - nur für denselben Deal, Vertrag und
  // dieselbe Variante, nie beim Bearbeiten (dort gälte der Editor-Stand), und
  // je Stand nur ein Versuch (scheitert das Füllen, kein Dauerversuch).
  useEffect(() => {
    const zuletzt = gefuelltMit.current;
    if (!zuletzt || !filledHtml || htmlEditMode || isExistingContractEditMode || previewing || finalizing) return undefined;
    if (zuletzt.deal !== dealId.trim() || zuletzt.vertrag !== stateKey || zuletzt.variante !== activeVariant.key) return undefined;
    if (gespraechsStand === zuletzt.stand || gespraechsStand === selbstVersucht.current) return undefined;
    const zeitgeber = window.setTimeout(() => {
      selbstVersucht.current = gespraechsStand;
      handleRunPreview({ vonSelbst: true });
    }, 600);
    return () => window.clearTimeout(zeitgeber);
  }, [activeVariant.key, dealId, filledHtml, finalizing, gespraechsStand, handleRunPreview, htmlEditMode, isExistingContractEditMode, previewing, stateKey]);

  const handleFinalizeContract = useCallback(async () => {
    if (!isAuthenticated) {
      toast.error('Bitte zuerst einloggen.');
      return;
    }
    if (istWerkvertrag(activeContract.key)) {
      toast.error(NICHT_HOCHLADEN_HINWEIS);
      return;
    }
    if (!dealId.trim()) {
      toast.error('Bitte eine Bitrix-Deal-ID eingeben.');
      return;
    }
    if (isExistingContractEditMode && !htmlEditMode) {
      toast.error('Bitte zuerst eine bereits erstellte HTML-Vertragsdatei laden.');
      return;
    }
    if (!templateHtml && !htmlEditMode) {
      toast.error('Vorlage noch nicht geladen.');
      return;
    }
    if (missingLeaseAnnexNumbers.length) {
      toast.error(`Für den Pachtvertrag als Anlage ${KLEINSPEICHER_LEASE_ANNEX_NUMBER} fehlt noch: ${missingLeaseAnnexNumbers.join(', ')}.`);
      return;
    }
    // Eine offene Lücke aus dem Gespräch („[Frist Baubeginn]“) geht nie zum
    // Kunden (seit 24.10.2026). Beim Bearbeiten zählt, was im Editor steht.
    let imEditor = null;
    if (htmlEditMode) {
      try { imEditor = readCurrentContractHtml() || ''; } catch (_error) { imEditor = ''; }
    }
    const offeneLuecken = offeneGespraechsLuecken({
      ersetzungen: gespraechsErsetzungen, klauseln: verhandlungsKlauseln, html: htmlEditMode ? imEditor : null,
    });
    if (offeneLuecken.length) {
      toast.error(`Nicht hochgeladen. ${lueckenSperrText(offeneLuecken)}`, { duration: 15000 });
      return;
    }

    setFinalizing(true);
    try {
      const leaseAnnexAttachment = await buildLeaseAnnexAttachment();
      const attachmentsForPayload = leaseAnnexAttachment
        ? [...attachmentPayload, leaseAnnexAttachment]
        : attachmentPayload;

      const startBackgroundFinalize = ({ payload, successMessage, applyResponse }) => {
        notifyManualUploadQueued(onManualUploadQueued, {
          optimistic: true,
          dealId: dealId.trim(),
          contractKey: stateKey,
        });
        toast.info('Upload wurde gestartet und läuft im Hintergrund – auch wenn du die Seite schließt.');

        axios.post(`${apiBase}/contracts/usage`, payload, { headers: authHeaders })
          .then((response) => {
            if (mountedRef.current && typeof applyResponse === 'function') {
              applyResponse(response);
            }
            rememberManualUploadBatch(response.data?.batchId);
            kickManualUploadWorker(apiBase, authHeaders);
            notifyManualUploadQueued(onManualUploadQueued, response.data);
            toast.success(successMessage(response));
          })
          .catch((error) => {
            console.error(error);
            const unresolvedFields = Array.isArray(error?.response?.data?.unresolved)
              ? error.response.data.unresolved
              : [];
            if (mountedRef.current && unresolvedFields.length > 0) {
              setUnresolved(unresolvedFields);
            }
            toast.error(error?.response?.data?.detail || error?.message || 'Upload in Bitrix fehlgeschlagen.');
          })
          .finally(() => {
            if (mountedRef.current) {
              setFinalizing(false);
            }
          });

        setFinalizing(false);
      };

      if (htmlEditMode) {
        const html = applyBuyerFieldsToAutomationHtml(readCurrentContractHtml());
        if (!html) {
          throw new Error('Kein bearbeiteter HTML-Inhalt vorhanden.');
        }
        const orderedVariants = [
          activeVariant,
          ...(uploadSiblingVariantForHtml
            ? activeContract.variants.filter((variant) => variant.key !== activeVariant.key)
            : []),
        ];
        const variantTemplates = await Promise.all(orderedVariants.map(async (variant) => {
          if (variant.key === activeVariant.key) {
            return {
              variant_key: variant.key,
              variant_label: variant.label,
            template_html: html,
            };
          }
          const loaded = await loadVariantTemplateHtml(variant);
          return {
            variant_key: variant.key,
            variant_label: variant.label,
            template_html: applyBuyerFieldsToAutomationHtml(applyUploadedContractToVariantTemplate(loaded.html, html)),
          };
        }));
        startBackgroundFinalize({
          payload: {
          action: 'am-contract-finalize-enqueue',
          contract_key: stateKey,
          contract_label: activeContract.label,
          deal_id: dealId.trim(),
          template_variant: activeVariant.key,
          variant_label: activeVariant.label,
          html,
          upload_basis: 'uploaded-html',
          variant_templates: variantTemplates,
          mapping: { fields: mappingFieldsForGeneration },
          clauses: selectedClauses,
          additionalOwners: additionalOwnersPayload,
          attachments: attachmentsForPayload,
          source_filename: uploadedHtmlName || undefined,
          },
          successMessage: () => 'Bearbeiteter Vertrag wurde geprüft und in die Upload-Warteschlange gelegt.',
          applyResponse: (response) => {
            setFilledHtml(response.data?.html || html);
            setPreviewSummary(response.data?.bitrixSummary || null);
            setUnresolved(Array.isArray(response.data?.unresolved) ? response.data.unresolved : []);
          },
        });
        return;
      }

      const orderedVariants = [
        activeVariant,
        ...activeContract.variants.filter((variant) => variant.key !== activeVariant.key),
      ];
      const geladeneVarianten = await Promise.all(orderedVariants.map(async (variant) => ({
        key: variant.key,
        label: variant.label,
        html: variant.key === activeVariant.key ? templateHtml : (await loadVariantTemplateHtml(variant)).html,
      })));
      // Zugestimmte Ersetzungen in JEDE Variante (seit 24.10.2026). Greift eine
      // irgendwo nicht, wird nicht hochgeladen - ein Vertrag ohne die vereinbarte
      // Änderung beim Kunden wäre schlimmer als ein Abbruch mit Grund.
      const { varianten: mitErsetzungen, fehler: ersetzungsFehler } = await mitGespraechsErsetzungen(geladeneVarianten);
      if (ersetzungsFehler) throw new Error(`Nicht hochgeladen. ${ersetzungsFehler}`);
      const variantTemplates = mitErsetzungen.map((variant) => ({
        variant_key: variant.key,
        variant_label: variant.label,
        template_html: applyBuyerFieldsToAutomationHtml(variant.html),
      }));
      const standBeimAbschliessen = gespraechsStandRef.current;

      startBackgroundFinalize({
        payload: {
        action: 'am-contract-finalize-enqueue',
        contract_key: stateKey,
        contract_label: activeContract.label,
        template_html: variantTemplates[0].template_html,
        deal_id: dealId.trim(),
        template_variant: activeVariant.key,
        variant_label: activeVariant.label,
        variant_templates: variantTemplates,
        mapping: { fields: mappingFieldsForGeneration },
        clauses: selectedClauses,
        additionalOwners: additionalOwnersPayload,
        attachments: attachmentsForPayload,
        },
        successMessage: (response) => {
          const contractNumber = response.data?.contractNumber ? ` (${response.data.contractNumber})` : '';
          return `Vertrag${contractNumber} wurde geprüft und in die Upload-Warteschlange gelegt.`;
        },
        applyResponse: (response) => {
          const html = response.data?.html;
          if (typeof html === 'string' && html) {
            setIframeSrc(null);
            setFilledHtml(html);
            setHtmlEditMode(false);
            setUploadedHtmlName('');
            setIframeSrcDoc(injectBaseHref(disableEditorInHtml(html), '/contracts/'));
            setIframeKey((v) => v + 1);
            gefuelltMit.current = {
              stand: standBeimAbschliessen, deal: dealId.trim(), vertrag: stateKey, variante: activeVariant.key,
            };
          }
          setPreviewSummary(response.data?.bitrixSummary || null);
          setUnresolved(Array.isArray(response.data?.unresolved) ? response.data.unresolved : []);
        },
      });
    } catch (error) {
      console.error(error);
      const unresolvedFields = Array.isArray(error?.response?.data?.unresolved)
        ? error.response.data.unresolved
        : [];
      if (unresolvedFields.length > 0) {
        setUnresolved(unresolvedFields);
      }
      toast.error(error?.response?.data?.detail || error?.message || 'Upload in Bitrix fehlgeschlagen.');
    } finally {
      setFinalizing(false);
    }
  }, [activeContract.key, activeContract.label, activeContract.variants, activeVariant, additionalOwnersPayload, apiBase, applyBuyerFieldsToAutomationHtml, attachmentPayload, authHeaders, buildLeaseAnnexAttachment, dealId, htmlEditMode, isAuthenticated, isExistingContractEditMode, loadVariantTemplateHtml, mappingFieldsForGeneration, missingLeaseAnnexNumbers, mitGespraechsErsetzungen, gespraechsErsetzungen, verhandlungsKlauseln, onManualUploadQueued, readCurrentContractHtml, selectedClauses, stateKey, templateHtml, uploadedHtmlName, uploadSiblingVariantForHtml]);

  const canFinalizeCurrentContract = isAuthenticated
    && !istWerkliefervertrag
    && Boolean(dealId.trim())
    && (!istAufhebungsvertrag || isExistingContractEditMode || vertragsendeVollstaendig(activeContract.key, aufhebungAngaben))
    && (isExistingContractEditMode
      ? (htmlEditMode && Boolean(filledHtml))
      : (Boolean(templateHtml) || (htmlEditMode && Boolean(filledHtml))));

  return (
    <div className="responsive-workspace responsive-workspace--contracts" style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* Der Kopf mit Überschrift und Erklärtext ist am 22.09.2026 entfallen.
          Er erklärte bei jedem Aufruf, was das Werkzeug tut - für jemanden, der
          täglich damit arbeitet, nur verschenkte Höhe über der Vorschau.
          Die Hinweise zur Feldsuche standen nur hier und sind deshalb nach
          AGENTS.md gewandert („Platzhalter im Vertrags-Mapping"). */}
      <div className="responsive-control-bar" style={{ padding: '12px 20px', borderBottom: `1px solid ${T.randWeich}`, background: T.grund, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 280 }}>
          <label style={FELD_BESCHRIFTUNG}>Vertragsart</label>
          <select
            value={activeContractKey}
            onChange={(event) => {
              const next = CONTRACTS.find((c) => c.key === event.target.value);
              setActiveContractKey(event.target.value);
              setUploadedContractInfo(null);
              if (next && !next.variants.find((v) => v.key === activeVariantKey)) {
                setActiveVariantKey(next.variants[0].key);
              }
            }}
            style={{ padding: '8px 10px', border: `1px solid ${T.rand}`, borderRadius: T.rFeld, background: T.flaeche, color: T.ink, fontSize: T.tTitel, fontWeight: 600, minHeight: 36 }}
          >
            {CONTRACTS.map((c) => (<option key={c.key} value={c.key}>{c.label}</option>))}
          </select>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={FELD_BESCHRIFTUNG}>Variante</label>
          <div style={{ display: 'flex', gap: 6 }}>
            {activeContract.variants.map((variant) => (
              <button
                key={variant.key}
                onClick={() => {
                  setActiveVariantKey(variant.key);
                  setUploadedContractInfo(null);
                }}
                style={{ padding: '8px 12px', border: '1px solid', borderColor: activeVariantKey === variant.key ? T.orange : T.rand, borderRadius: T.rKnopf, background: activeVariantKey === variant.key ? T.orange : T.flaeche, color: activeVariantKey === variant.key ? T.flaeche : T.ink, fontSize: T.tTitel, fontWeight: 700, cursor: 'pointer', minHeight: 36 }}
              >
                {variant.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Bis zum 23.09.2026 stand hier immer eine Zeile, woher die Vorlage
          kommt ("Vorlage aus Vorlagen > Vertraege geladen. Mapping bleibt
          erhalten ..."). Das ist der Normalfall und sagt nichts. Jetzt nur
          noch, wenn es keinen gespeicherten Stand gibt. */}
      {!isExistingContractEditMode && !loadingTemplate && templateSource === 'original' ? (
        <div style={{ padding: '8px 20px', background: T.warnGrund, color: T.warn, fontSize: T.tMeta, borderBottom: `1px solid ${T.warnRand}` }}>
          Unter „Vorlagen › Verträge“ ist für diese Vorlage noch kein Stand gespeichert – es gilt die Originaldatei.
        </div>
      ) : null}
      {!isExistingContractEditMode && !loadingTemplate && templateSource === 'fehler' && vorlagenFehler ? (
        <div style={{ padding: '8px 20px', background: T.gefahrGrund, color: T.gefahr, fontSize: T.tMeta, borderBottom: `1px solid ${T.gefahrRand}` }}>
          {vorlagenFehler}
        </div>
      ) : null}

      {htmlEditMode ? (
        <div style={{ padding: '8px 20px', background: T.gutGrund, color: T.gut, borderBottom: `1px solid ${T.gutRand}`, fontSize: T.tMeta, fontWeight: 700 }}>
          Du bearbeitest den Vertrag dieses Deals{uploadedHtmlName ? ` (${uploadedHtmlName})` : ''} – die Vorlage bleibt unverändert.
        </div>
      ) : null}

      {isExistingContractEditMode && htmlEditMode && uploadedContractInfo ? (
        <div style={{ padding: '8px 20px', background: T.infoGrund, color: T.info, borderBottom: `1px solid ${T.infoRand}`, fontSize: T.tMeta, fontWeight: 700 }}>
          Erkannt: {uploadedContractInfo.contractLabel} - {uploadedContractInfo.variantLabel}. Passus-Auswahl und Einfügepositionen werden für diese Vertragsart geladen.
        </div>
      ) : null}

      <div className="responsive-split-workspace" style={{ flex: 1, minHeight: 0, display: 'flex', overflow: 'hidden' }}>
        {/* Gespräch ganz links: eigene Spalte über die volle Höhe. In der
            Einstellungsspalte rechts war es zu schmal, um darin zu arbeiten. */}
        <VerhandlungsPassus
          apiBase={apiBase}
          authHeaders={authHeaders}
          dealId={dealId}
          contractKey={activeContract.key}
          contractLabel={activeContract.label}
          abschnitte={vertragsAbschnitte}
          // Beim Erstellen die Vorlage (auch nach dem Füllen - dort greifen die
          // Ersetzungen), beim Bearbeiten allein der bearbeitete Vertrag. Der
          // Server macht daraus Text — hier wird nichts neu serialisiert.
          vertragsHtml={gespraech.html}
          // Frisch vor jedem Aufruf: beim Bearbeiten der Stand im Editor.
          holeVertragsHtml={holeGespraechsHtml}
          isAuthenticated={isAuthenticated}
          onKlauselnChange={setVerhandlungsKlauseln}
          onErsetzungenChange={setGespraechsErsetzungen}
          // Nur beim Bearbeiten: übernimmt Ersetzungen in den Vertrag im
          // Editor — nie in die geteilte Vorlage. Beim Erstellen kommen sie
          // mit Füllen und Abschließen hinein (mitGespraechsErsetzungen).
          onVertragGeaendert={gespraech.ersetzungenIn === 'vertrag' ? uebernimmGespraechsHtml : null}
          // Beim Erstellen kommen freigegebene Ergänzungen mit „Füllen“ in
          // den Vertrag. Beim Bearbeiten gibt es kein Füllen - dort fügt sie
          // dieser Knopf in den Vertrag im Editor ein.
          onErgaenzungenEinfuegen={htmlEditMode ? handleApplyClausesToEditedHtml : null}
          // Gebaut am 23.09.2026 (bad4ca0), aber nie uebergeben - der Knopf
          // "In der Vorschau" erschien deshalb nie.
          onSpringeZuStelle={springeZuStelle}
          bearbeitet={gespraech.ersetzungenIn === 'vertrag'}
          erstellen={gespraech.ersetzungenIn === 'vorlage'}
        />

        {/* Preview */}
        <div className="responsive-preview-pane" style={{ flex: 1, minWidth: 0, background: '#E8EEF6', position: 'relative', overflowY: 'auto', overflowX: 'hidden' }}>
          {iframeSrcDoc ? (
            <iframe
              key={iframeKey}
              ref={iframeRef}
              onLoad={handlePreviewIframeLoad}
              scrolling="no"
              title={`${activeContract.label} — Vorschau`}
              srcDoc={iframeSrcDoc}
              style={{ width: '100%', height: iframeHeight, border: 0, display: 'block' }}
            />
          ) : iframeSrc ? (
            <iframe
              key={iframeKey}
              ref={iframeRef}
              onLoad={handlePreviewIframeLoad}
              scrolling="no"
              title={`${activeContract.label} — Vorschau`}
              src={iframeSrc}
              style={{ width: '100%', height: iframeHeight, border: 0, display: 'block' }}
            />
          ) : (
            <div style={{ minHeight: 560, display: 'grid', placeItems: 'center', padding: 24 }}>
              <div style={{ maxWidth: 520, background: '#FFFFFF', border: '1px solid #CBD5E1', borderRadius: 12, padding: 20, boxShadow: '0 18px 45px rgba(15,23,42,0.08)' }}>
                <div style={{ color: '#122A5C', fontFamily: 'Outfit, sans-serif', fontSize: 18, fontWeight: 700, letterSpacing: '-0.03em', marginBottom: 8 }}>
                  {isExistingContractEditMode ? 'HTML-Vertrag hochladen' : 'Vorlage wird geladen'}
                </div>
                <div style={{ color: '#64748B', fontSize: 13, lineHeight: 1.5 }}>
                  {isExistingContractEditMode
                    ? 'Rechts die Deal-ID eingeben und die HTML-Datei des Vertrags laden.'
                    : 'Gleich erscheint sie hier.'}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Mapping-Panel rechts */}
        <aside className="responsive-side-panel" style={{ width: 360, flexShrink: 0, borderLeft: `1px solid ${T.randWeich}`, background: T.flaeche, minHeight: 0, overflowY: 'auto' }}>
          <div style={{ padding: '14px 16px', borderBottom: `1px solid ${T.randWeich}`, background: T.grund }}>
            {/* Stand bis zum 23.09.2026 innerhalb der Versalien-Ueberschrift
                "LIVE-VORSCHAU" und erbte deren Sperrung. */}
            {entwurf ? (
              <div style={{
                marginBottom: 12, padding: '9px 11px', borderRadius: T.rChip, background: T.warnGrund,
                borderLeft: `3px solid ${T.orange}`, fontSize: T.tMeta, color: T.ink, lineHeight: 1.5,
              }}>
                <strong style={{ color: T.orangeTinte }}>Nicht gespeicherter Entwurf</strong>
                {' '}({entwurf.contractLabel || 'Vertrag'}{entwurf.dealId ? `, Deal ${entwurf.dealId}` : ''}, {new Date(entwurf.ts).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })})
                <div style={{ display: 'flex', gap: 12, marginTop: 7, alignItems: 'center' }}>
                  <button type="button" onClick={handleEntwurfWiederherstellen} style={{ ...knopfStil(true), padding: '4px 10px' }}>Wiederherstellen</button>
                  <button type="button" onClick={() => { try { localStorage.removeItem(ENTWURF_KEY); } catch (_e) {} setEntwurf(null); }} style={textKnopfStil}>Verwerfen</button>
                </div>
              </div>
            ) : null}
            <input
              ref={fileInputRef}
              type="file"
              accept=".html,.htm,text/html"
              onChange={handleImportHtmlFile}
              style={{ display: 'none' }}
            />
            <input
              ref={lageplanFileInputRef}
              type="file"
              accept="image/*,.png,.jpg,.jpeg,.webp,.gif,.svg"
              onChange={handleReplaceLageplanFromFile}
              style={{ display: 'none' }}
            />
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input
                value={dealId}
                onChange={(e) => setDealId(e.target.value)}
                placeholder="Deal-ID"
                aria-label="Bitrix Deal-ID"
                style={{ flex: 1, padding: '8px 10px', border: `1px solid ${T.rand}`, borderRadius: T.rFeld, fontSize: 13, minHeight: 36 }}
              />
              {!isExistingContractEditMode ? (
                <button
                  onClick={handleRunPreview}
                  disabled={previewing || !isAuthenticated || !dealId.trim()}
                  title={isAuthenticated ? 'Felder aus Bitrix holen und Vorschau füllen' : 'Bitte erst einloggen'}
                  style={{ padding: '8px 12px', border: '1px solid #122A5C', borderRadius: 8, background: previewing ? '#CBD5E1' : '#122A5C', color: '#FFFFFF', fontSize: 12, fontWeight: 700, cursor: previewing || !isAuthenticated || !dealId.trim() ? 'not-allowed' : 'pointer', opacity: previewing || !isAuthenticated || !dealId.trim() ? 0.6 : 1, minHeight: 36, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                >
                  <Play size={12} />
                  {previewing ? '…' : 'Füllen'}
                </button>
              ) : null}
              <button
                onClick={handleResetPreview}
                title="Auf leere Vorlage zurücksetzen"
                aria-label="Auf leere Vorlage zurücksetzen"
                style={{ padding: '8px 10px', border: '1px solid #CBD5E1', borderRadius: 8, background: '#FFFFFF', color: '#475569', fontSize: 12, fontWeight: 700, cursor: 'pointer', minHeight: 36 }}
              >
                <RotateCcw size={12} />
              </button>
            </div>
            {isExistingContractEditMode ? (
              <div style={{ marginTop: 8, display: 'flex', gap: 6 }}>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  title="Bereits erzeugte HTML-Vertragsdatei hochladen und individuell bearbeiten"
                  style={{ flex: 1, padding: '8px 10px', border: '1px solid #122A5C', borderRadius: 8, background: '#FFFFFF', color: '#122A5C', fontSize: 12, fontWeight: 700, cursor: 'pointer', minHeight: 36, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                >
                  <Upload size={13} />
                  HTML-Vertrag laden
                </button>
              </div>
            ) : (filledHtml && !htmlEditMode ? (
              <div style={{ marginTop: 8, display: 'flex', gap: 6 }}>
                <button
                  type="button"
                  onClick={handleEditFilledContract}
                  title="Aktuell gefüllten Vertrag individuell bearbeiten"
                  style={{ flex: 1, padding: '7px 10px', border: '1px solid #F28A0D', borderRadius: 8, background: '#FFF7ED', color: '#9A4A00', fontSize: 12, fontWeight: 700, cursor: 'pointer', minHeight: 34, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                >
                  <Pencil size={13} />
                  Bearbeiten
                </button>
              </div>
            ) : null)}
            {filledHtml ? (
              <button
                type="button"
                onClick={handleRefreshMappedFieldsFromDeal}
                disabled={refreshingMappedFields || previewing || !isAuthenticated || !dealId.trim()}
                title="Nur bekannte Mapping-Stellen aus der Vorlage mit Daten der Deal-ID aktualisieren"
                style={{ marginTop: 8, width: '100%', padding: '8px 10px', border: '1px solid #122A5C', borderRadius: 8, background: refreshingMappedFields || previewing || !isAuthenticated || !dealId.trim() ? '#CBD5E1' : '#EEF4FF', color: '#122A5C', fontSize: 12, fontWeight: 700, cursor: refreshingMappedFields || previewing || !isAuthenticated || !dealId.trim() ? 'not-allowed' : 'pointer', minHeight: 36, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, opacity: refreshingMappedFields || previewing || !isAuthenticated || !dealId.trim() ? 0.7 : 1 }}
              >
                <RotateCcw size={13} />
                {refreshingMappedFields ? 'Aktualisiere...' : 'Deal-Daten aktualisieren'}
              </button>
            ) : null}
            {filledHtml && currentLageplanInfo.available ? (
              <div style={{ marginTop: 10, border: '1px solid #D7E2F1', borderRadius: 8, background: '#FFFFFF', overflow: 'hidden' }}>
                <div style={{ padding: '8px 10px', borderBottom: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', gap: 6, color: '#122A5C', fontSize: 11, fontWeight: 700 }}>
                  <ImageIcon size={13} />
                  Lageplan
                  <span style={{ marginLeft: 'auto', color: currentLageplanInfo.hasImage ? '#0F766E' : '#B45309', fontSize: 10, fontWeight: 700 }}>
                    {currentLageplanInfo.hasImage ? 'Bild erkannt' : 'ohne Bild'}
                  </span>
                </div>
                <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {/* Die Bitrix-Kennung braucht man nur, wenn sie fehlt oder
                      falsch ist - sonst eingeklappt. */}
                  {lageplanFeldOpen || !String(mappingDraft[LAGEPLAN_PLACEHOLDER] || '').trim() ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <label style={FELD_BESCHRIFTUNG}>Bitrix-Feld</label>
                      <input
                        list="bitrix-field-hints"
                        value={mappingDraft[LAGEPLAN_PLACEHOLDER] || ''}
                        onChange={(event) => handleMappingChange(LAGEPLAN_PLACEHOLDER, event.target.value)}
                        placeholder="z.B. DEAL.UF_CRM_..."
                        style={{ width: '100%', padding: '6px 8px', border: `1px solid ${T.rand}`, borderRadius: 7, fontSize: 11, fontFamily: 'monospace' }}
                      />
                    </div>
                  ) : null}
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button
                      type="button"
                      onClick={handleApplyLageplanMapping}
                      disabled={lageplanApplying || !isAuthenticated || !dealId.trim() || !String(mappingDraft[LAGEPLAN_PLACEHOLDER] || '').trim()}
                      title={'Lageplan-Bild per Mapping aus dem Bitrix-Deal neu \u00fcbernehmen'}
                      style={{ flex: 1, padding: '7px 8px', border: '1px solid #122A5C', borderRadius: 7, background: lageplanApplying || !isAuthenticated || !dealId.trim() || !String(mappingDraft[LAGEPLAN_PLACEHOLDER] || '').trim() ? '#CBD5E1' : '#122A5C', color: '#FFFFFF', fontSize: 11, fontWeight: 700, cursor: lageplanApplying || !isAuthenticated || !dealId.trim() || !String(mappingDraft[LAGEPLAN_PLACEHOLDER] || '').trim() ? 'not-allowed' : 'pointer', minHeight: 32 }}
                    >
                      {lageplanApplying ? 'Lade...' : 'Aus Deal \u00fcbernehmen'}
                    </button>
                    <button
                      type="button"
                      onClick={() => lageplanFileInputRef.current?.click()}
                      title="Lageplan-Bild lokal ersetzen"
                      style={{ padding: '7px 9px', border: '1px solid #CBD5E1', borderRadius: 7, background: '#F8FAFC', color: '#122A5C', fontSize: 11, fontWeight: 700, cursor: 'pointer', minHeight: 32, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}
                    >
                      <Upload size={12} />
                      Bilddatei
                    </button>
                  </div>
                  {String(mappingDraft[LAGEPLAN_PLACEHOLDER] || '').trim() ? (
                    <div>
                      <Aufklapper offen={lageplanFeldOpen} onClick={() => setLageplanFeldOpen((open) => !open)}>Bitrix-Feld</Aufklapper>
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}
            {previewSummary ? (
              <div style={{ marginTop: 10, padding: '8px 11px', background: T.flaeche, borderRadius: T.rChip, borderLeft: `3px solid ${unresolved.length ? T.warn : T.gut}`, fontSize: T.tMeta, color: T.body, lineHeight: 1.5 }}>
                <div style={{ fontWeight: 700, color: T.ink }}>
                  {previewSummary.dealTitle || `Deal #${previewSummary.dealId}`}
                </div>
                <div style={{ fontSize: T.tMikro, color: T.muted }}>
                  #{previewSummary.dealId}{previewSummary.contactName ? ` · ${previewSummary.contactName}` : ''}
                </div>
                {unresolved.length > 0 ? (
                  <div style={{ marginTop: 4, color: T.warn, fontSize: T.tMikro }} title={unresolved.join(', ')}>
                    <strong>Nicht gefüllt:</strong> {unresolved.slice(0, 6).join(', ')}{unresolved.length > 6 ? ` … (+${unresolved.length - 6})` : ''}
                  </div>
                ) : null}
              </div>
            ) : null}
            {filledHtml ? (
              <div style={{ marginTop: 8, display: 'flex', gap: 6 }}>
                <button
                  onClick={handleDownloadCurrentHtml}
                  title="Gefüllten Vertrag als HTML-Datei herunterladen"
                  style={{ flex: 1, padding: '7px 10px', border: '1px solid #122A5C', borderRadius: 8, background: '#FFFFFF', color: '#122A5C', fontSize: 12, fontWeight: 700, cursor: 'pointer', minHeight: 34, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                >
                  <Download size={13} />
                  HTML
                </button>
                <button
                  onClick={handlePrintCurrentPdf}
                  title="Gefüllten Vertrag drucken / als PDF speichern (Browser-Druckdialog)"
                  style={{ flex: 1, padding: '7px 10px', border: '1px solid #122A5C', borderRadius: 8, background: '#122A5C', color: '#FFFFFF', fontSize: 12, fontWeight: 700, cursor: 'pointer', minHeight: 34, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                >
                  <Printer size={13} />
                  PDF / Drucken
                </button>
              </div>
            ) : null}
            {htmlEditMode && htmlUploadSiblingVariant && !istWerkliefervertrag ? (
              <label
                title={`${htmlUploadSiblingVariant.label} aus dem bearbeiteten HTML erzeugen und gemeinsam als HTML/PDF in Bitrix hochladen`}
                style={{ marginTop: 8, padding: '8px 10px', border: '1px solid #D7E2F1', borderRadius: 8, background: '#F8FAFC', color: '#122A5C', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', lineHeight: 1.35 }}
              >
                <input
                  type="checkbox"
                  checked={uploadSiblingVariantForHtml}
                  onChange={(event) => setUploadSiblingVariantForHtml(event.target.checked)}
                  style={{ marginTop: 2 }}
                />
                <span>
                  {htmlUploadSiblingVariant.key === 'druckversion'
                    ? 'Druckversion zusätzlich aus der bearbeiteten CI-Version erstellen und hochladen'
                    : `${htmlUploadSiblingVariant.label} zusätzlich aus der bearbeiteten Variante erstellen und hochladen`}
                  <span style={{ display: 'block', marginTop: 2, color: '#64748B', fontWeight: 700 }}>
                    HTML und PDF werden in dieselben Bitrix-Vertragsfelder geschrieben.
                  </span>
                </span>
              </label>
            ) : null}
            {/* Erst mit Deal-ID - vorher gibt es nichts abzuschliessen, und ein
                ausgegrauter Hauptknopf lenkt vom eigentlichen ersten Schritt ab. */}
            {istWerkliefervertrag && (dealId.trim() || htmlEditMode) ? (
              <div style={{ marginTop: 10, padding: '8px 10px', border: '1px solid #D7E2F1', borderRadius: 8, background: '#F8FAFC', color: '#334155', fontSize: 11, lineHeight: 1.4 }}>
                {NICHT_HOCHLADEN_HINWEIS}
              </div>
            ) : null}
            {(dealId.trim() || htmlEditMode) && !istWerkliefervertrag ? (
              <button
                onClick={handleFinalizeContract}
                disabled={finalizing || previewing || !canFinalizeCurrentContract}
                title={isAuthenticated ? (isExistingContractEditMode ? 'Bearbeiteten HTML-Vertrag in Bitrix hochladen' : 'Vertrag generieren, PDF in Bitrix hochladen und Deal-Phase weiterführen') : 'Bitte erst einloggen'}
                style={{ ...primaerKnopfStil, marginTop: 10, width: '100%', justifyContent: 'center', padding: '9px 10px', fontSize: 12, minHeight: 38, cursor: finalizing || previewing || !canFinalizeCurrentContract ? 'not-allowed' : 'pointer', opacity: finalizing || previewing || !canFinalizeCurrentContract ? 0.45 : 1 }}
              >
                <CheckCircle2 size={14} />
                {finalizing ? 'Wird hochgeladen …' : (isExistingContractEditMode || htmlEditMode ? 'Bearbeiteten Vertrag hochladen' : 'Abschließen und in Bitrix hochladen')}
              </button>
            ) : null}

            {istAufhebungsvertrag && !isExistingContractEditMode ? (
              <AufhebungAngaben
                vertragKey={activeContract.key}
                istKuendigung={istKuendigung(activeContract.key)}
                angaben={aufhebungAngaben}
                onAendern={handleAufhebungAendern}
                vorschlagHinweis={aufhebungHinweis}
              />
            ) : null}

            {istWerkliefervertrag && !isExistingContractEditMode ? (
              <WerkvertragAngaben
                angaben={werkvertragAngaben}
                onAendern={handleWerkvertragAendern}
                onVerkaeufer={handleVerkaeuferWaehlen}
                onAuftragnehmer={handleAuftragnehmerAendern}
                onEeg={handleEegAbsicherung}
                eegBausteinFehlt={Boolean(templateHtml) && !enthaeltEegBaustein(templateHtml)}
              />
            ) : null}

            {isBuyerPartnerFlow ? (
              <div style={{ marginTop: 10, border: '1px solid #D7E2F1', borderRadius: 8, background: '#FFFFFF', overflow: 'hidden' }}>
                <div style={{ padding: '8px 10px', borderBottom: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', gap: 6, color: '#122A5C', fontSize: 11, fontWeight: 700 }}>
                  <Users size={13} />
                  {istWerkliefervertrag ? 'Auftraggeber' : 'Käufer / Vertragspartner'}
                  <span style={{ marginLeft: 'auto', color: hasBuyerValues(buyerDraft) ? '#0F766E' : '#B45309', fontSize: 10, fontWeight: 700 }}>
                    {hasBuyerValues(buyerDraft) ? 'manuell' : 'offen'}
                  </span>
                </div>
                <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <select
                      value={selectedBuyerId}
                      onChange={(event) => handleSelectBuyer(event.target.value)}
                      style={{ flex: 1, minHeight: 32, padding: '6px 8px', border: '1px solid #CBD5E1', borderRadius: 7, fontSize: 11, color: '#0F172A', background: '#FFFFFF' }}
                    >
                      <option value="">Standardkunde wählen...</option>
                      {buyerLibrary.map((buyer) => (
                        <option key={buyer.id} value={buyer.id}>{getBuyerDisplayName(buyer)}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={handleResetBuyerDraft}
                      title="Individuellen Käufer ohne Standardkunde neu eingeben"
                      style={{ padding: '6px 8px', border: '1px solid #CBD5E1', borderRadius: 7, background: '#F8FAFC', color: '#122A5C', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
                    >
                      Neu
                    </button>
                  </div>
                  <textarea
                    value={kaeuferText}
                    onChange={(event) => setKaeuferText(event.target.value)}
                    placeholder="Daten einfügen (Adressblock, E-Mail-Signatur, Impressum …) – die Felder füllen sich daraus."
                    rows={3}
                    style={{ width: '100%', padding: '6px 8px', border: '1px dashed #94A3B8', borderRadius: 7, fontSize: 11, color: '#0F172A', resize: 'vertical', fontFamily: 'inherit' }}
                  />
                  {kaeuferText.trim() ? (
                    <button
                      type="button"
                      onClick={handleKaeuferAusText}
                      title="Erkannte Angaben in die Felder übernehmen – ersetzt die bisherigen Eingaben"
                      style={{ padding: '6px 8px', border: '1px solid #122A5C', borderRadius: 7, background: '#FFFFFF', color: '#122A5C', fontSize: 11, fontWeight: 700, cursor: 'pointer', minHeight: 30 }}
                    >
                      Felder füllen
                    </button>
                  ) : null}
                  {kaeuferErkennung ? (
                    <div style={{ color: kaeuferErkennung.erkannt.length ? T.muted : T.warn, fontSize: 10.5, lineHeight: 1.4 }}>
                      {kaeuferErkennung.erkannt.length
                        ? `Erkannt: ${kaeuferErkennung.erkannt.map((feld) => KAEUFER_FELDNAMEN[feld] || feld).join(', ')} – bitte prüfen.`
                        : 'Nichts erkannt – Felder unverändert.'}
                      {kaeuferErkennung.uebrig.length ? (
                        <span style={{ display: 'block', color: T.warn }}>Nicht zugeordnet: {kaeuferErkennung.uebrig.join(' · ')}</span>
                      ) : null}
                    </div>
                  ) : null}
                  {/* Der Werkliefervertrag nutzt nur Name/Firma und Anschrift (AUFTRAGGEBER_FELDER) -
                      andere Felder kaemen im Vertrag nicht an. */}
                  {[
                    ['label', 'Anzeigename Standardkunde'],
                    ['company', istWerkliefervertrag ? 'Name / Firma (bei Firmen mit „vertreten durch …“)' : 'Firma / Käufer'],
                    ['representatives', 'Vertretungsberechtigte Person'],
                    ['street', 'Straße und Hausnummer'],
                    ['postalCity', 'PLZ und Ort'],
                    ['registerCourt', 'Registergericht'],
                    ['hrb', 'HRB / Register-Nr.'],
                    ['phone', 'Telefon'],
                    ['mobile', 'Mobil'],
                    ['email', 'E-Mail'],
                    ['vatId', 'USt-ID'],
                    ['taxNumber', 'Steuernummer'],
                    ['legalForm', 'Geschäftsform'],
                    ['seat', 'Sitz der Gesellschaft'],
                  ].filter(([field]) => !istWerkliefervertrag || AUFTRAGGEBER_FELDER.includes(field)).map(([field, placeholder]) => (
                    <input
                      key={field}
                      value={buyerDraft[field] || ''}
                      onChange={(event) => handleBuyerDraftChange(field, event.target.value)}
                      placeholder={placeholder}
                      style={{ width: '100%', minHeight: 30, padding: '6px 8px', border: '1px solid #CBD5E1', borderRadius: 7, fontSize: 11, color: '#0F172A' }}
                    />
                  ))}
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button
                      type="button"
                      onClick={handleSaveBuyerAsStandard}
                      style={{ flex: 1, padding: '7px 8px', border: '1px solid #122A5C', borderRadius: 7, background: '#122A5C', color: '#FFFFFF', fontSize: 11, fontWeight: 700, cursor: 'pointer', minHeight: 32 }}
                    >
                      Als Standard speichern
                    </button>
                    <button
                      type="button"
                      onClick={handleDeleteSelectedBuyer}
                      disabled={!selectedBuyerId}
                      style={{ padding: '7px 9px', border: '1px solid #FECACA', borderRadius: 7, background: selectedBuyerId ? '#FEF2F2' : '#F8FAFC', color: selectedBuyerId ? '#B91C1C' : '#94A3B8', fontSize: 11, fontWeight: 700, cursor: selectedBuyerId ? 'pointer' : 'not-allowed', minHeight: 32 }}
                    >
                      Löschen
                    </button>
                  </div>
                  <div style={{ color: T.muted, fontSize: 10.5, lineHeight: 1.35 }}>
                    Gilt nur für diesen Vertrag – außer du speicherst {istWerkliefervertrag ? 'den Auftraggeber' : 'den Käufer'} als Standard.
                  </div>
                </div>
              </div>
            ) : null}

            <div style={{ marginTop: 10, border: '1px solid #E2E8F0', borderRadius: 8, background: '#FFFFFF', overflow: 'hidden' }}>
              <div style={{ padding: '8px 10px', borderBottom: additionalOwnersDraft.length ? '1px solid #E2E8F0' : 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#475569', fontSize: 11, fontWeight: 700 }}>
                  <Users size={13} />
                  Weitere Eigentümer
                  {additionalOwnersPayload.length ? <span style={{ color: '#0F766E' }}>({additionalOwnersPayload.length})</span> : null}
                </div>
                <button
                  type="button"
                  onClick={handleAddAdditionalOwner}
                  style={{ padding: '5px 8px', border: '1px solid #CBD5E1', borderRadius: 7, background: '#F8FAFC', color: '#122A5C', fontSize: 11, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                  title="Zusätzlichen Eigentümer mit eigener Adresse eintragen"
                >
                  <Plus size={12} />
                  Hinzufügen
                </button>
              </div>

              {additionalOwnersDraft.length ? (
                <div style={{ maxHeight: 320, overflowY: 'auto', padding: 10, display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {additionalOwnersDraft.map((owner, index) => {
                    const inputStyle = { width: '100%', minHeight: 30, padding: '6px 8px', border: '1px solid #CBD5E1', borderRadius: 7, fontSize: 11, color: '#0F172A' };
                    return (
                      <div key={owner.id} style={{ border: '1px solid #E2E8F0', borderRadius: 8, padding: 8, background: '#F8FAFC', display: 'flex', flexDirection: 'column', gap: 7 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                          <div style={{ color: '#122A5C', fontSize: 11, fontWeight: 700 }}>Eigentümer {index + 2}</div>
                          <button
                            type="button"
                            onClick={() => handleRemoveAdditionalOwner(owner.id)}
                            style={{ width: 26, height: 26, border: '1px solid #FECACA', borderRadius: 7, background: '#FEF2F2', color: '#B91C1C', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                            title="Eigentümer entfernen"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                        <input value={owner.company} onChange={(event) => handleAdditionalOwnerChange(owner.id, 'company', event.target.value)} placeholder="Firma (optional)" style={inputStyle} />
                        <input value={owner.name} onChange={(event) => handleAdditionalOwnerChange(owner.id, 'name', event.target.value)} placeholder="Name / Vertretungsberechtigte Person" style={inputStyle} />
                        <input value={owner.street} onChange={(event) => handleAdditionalOwnerChange(owner.id, 'street', event.target.value)} placeholder="Straße und Hausnummer" style={inputStyle} />
                        <input value={owner.postalCity} onChange={(event) => handleAdditionalOwnerChange(owner.id, 'postalCity', event.target.value)} placeholder="PLZ und Ort" style={inputStyle} />
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                          <input value={owner.email} onChange={(event) => handleAdditionalOwnerChange(owner.id, 'email', event.target.value)} placeholder="E-Mail" style={inputStyle} />
                          <input value={owner.phone} onChange={(event) => handleAdditionalOwnerChange(owner.id, 'phone', event.target.value)} placeholder="Telefon" style={inputStyle} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </div>
          </div>

          <AnlagenAuswahl
            anlagen={anlagenBausteine}
            gewaehlteIds={selectedClauseIds}
            nummern={anlagenNummernJeBaustein}
            onUmschalten={handleAnlageUmschalten}
            dealId={dealId}
            dealDateien={dealDateien}
            laden={dealDateienLaden}
            fehler={dealDateienFehler}
            onNeuLaden={() => setDealDateienAnstoss((n) => n + 1)}
            auswahl={anlagenAuswahl}
            onAuswahl={setAnlagenAuswahl}
            apiBase={apiBase}
            authHeaders={authHeaders}
          />

          {paragraphenBausteine.length > 0 || (htmlEditMode && anlagenBausteine.length > 0) ? (
            <div style={{ padding: '10px 16px', borderBottom: '1px solid #E2E8F0', background: '#F8FAFC' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                <ListChecks size={13} color="#475569" />
                <span style={{ color: '#475569', fontSize: 11, fontWeight: 700 }}>Optionale Paragraphen</span>
              </div>
              {paragraphenBausteine.map((clause) => {
                const checked = selectedClauseIds.includes(clause.id);
                const clauseMappings = normalizeClauseDefaultMapping(clause.default_mapping || clause.defaultMapping);
                const mappingEntries = Object.entries(clauseMappings);
                const clausePlaceholders = extractClausePlaceholders(clause.content || '');
                const missingDefaultMappings = clausePlaceholders.filter((placeholder) => !String(clauseMappings[placeholder] || '').trim());
                return (
                  <label key={clause.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 6, cursor: 'pointer' }}>
                    <input type="checkbox" checked={checked} onChange={() => setSelectedClauseIds((prev) => checked ? prev.filter((id) => id !== clause.id) : [...prev, clause.id])} style={{ marginTop: 2, flexShrink: 0 }} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#0F172A' }}>{clause.title}</div>
                      <div style={{ fontSize: 10, color: '#64748B' }}>{clause.insert_position === 'before-signatures' ? 'Vor Unterschriften' : clause.insert_position === 'after-preamble' ? 'Nach Praembel' : 'Ende des Vertrags'}</div>
                      {/* Die Zuordnung je Passus ("{{x}} -> DEAL.UF_CRM_...") stand
                          bis zum 23.09.2026 hier - sie gehoert in die
                          Paragraphen-Verwaltung. Hier nur, wenn eine fehlt. */}
                      {mappingEntries.length === 0 && missingDefaultMappings.length > 0 ? (
                        <div style={{ marginTop: 2, fontSize: 10, color: T.warn }}
                          title={missingDefaultMappings.map((placeholder) => `{{${placeholder}}}`).join(', ')}>
                          Feldzuordnung fehlt ({missingDefaultMappings.length})
                        </div>
                      ) : null}
                    </div>
                  </label>
                );
              })}
              {htmlEditMode ? (
                <button
                  type="button"
                  onClick={handleApplyClausesToEditedHtml}
                  disabled={previewing || selectedClauses.length === 0 || !dealId.trim()}
                  title={dealId.trim() ? 'Ausgewählte Passus in den aktuell bearbeiteten HTML-Vertrag einfügen' : 'Bitte zuerst eine Deal-ID eingeben'}
                  style={{
                    marginTop: 8,
                    width: '100%',
                    padding: '8px 10px',
                    border: '1px solid #F28A0D',
                    borderRadius: 8,
                    background: previewing || selectedClauses.length === 0 || !dealId.trim() ? '#FFE8CC' : '#FFF7ED',
                    color: previewing || selectedClauses.length === 0 || !dealId.trim() ? '#9A6B35' : '#9A4A00',
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: previewing || selectedClauses.length === 0 || !dealId.trim() ? 'not-allowed' : 'pointer',
                    minHeight: 34,
                  }}
                >
                  {previewing ? 'Füge ein...' : `Ausgewählte Passus einfügen${selectedClauses.length ? ` (${selectedClauses.length})` : ''}`}
                </button>
              ) : null}
            </div>
          ) : null}

          <div style={{ padding: '10px 16px', borderBottom: `1px solid ${T.randWeich}`, display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              type="button"
              onClick={() => setMappingOpen((open) => !open)}
              aria-expanded={mappingOpen}
              style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6, border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', color: T.body, fontSize: 11, fontWeight: 700, textAlign: 'left' }}
            >
              {mappingOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
              <Link2 size={13} />
              <span style={{ flex: 1 }}>Felder-Mapping</span>
              {/* Eingeklappt soll man trotzdem sehen, dass etwas aufzuraeumen
                  ist (Code-Review 23.09.2026). */}
              {nichtInVorlage > 0 ? (
                <span style={{ color: T.warn, fontWeight: 700 }} title="Gespeicherte Zuordnungen, deren Platzhalter in der geladenen Vorlage nicht vorkommt – sie bleiben gespeichert">
                  {nichtInVorlage} nicht in Vorlage
                </span>
              ) : null}
              <span style={{ color: T.muted, fontWeight: 600 }}>{mappingListe.length}</span>
            </button>
            {mappingOpen ? (
              <button
                onClick={handleSaveMapping}
                disabled={saving || !isAuthenticated || mappingLadefehler || mappingGeladenFuer !== stateKey}
                title={mappingLadefehler || mappingGeladenFuer !== stateKey
                  ? 'Gesperrt, bis das gespeicherte Mapping geladen ist'
                  : mappingSavedAt ? `Zuletzt gespeichert: ${new Date(mappingSavedAt).toLocaleString('de-DE')}` : 'Noch nicht gespeichert'}
                style={{ ...knopfStil(true), padding: '5px 9px', cursor: saving || !isAuthenticated || mappingLadefehler || mappingGeladenFuer !== stateKey ? 'not-allowed' : 'pointer', opacity: saving || !isAuthenticated || mappingLadefehler || mappingGeladenFuer !== stateKey ? 0.6 : 1 }}
              >
                <Save size={11} />
                {saving ? '…' : 'Speichern'}
              </button>
            ) : null}
          </div>

          {mappingOpen ? (
          <div style={{ padding: '8px 0', borderBottom: `1px solid ${T.randWeich}` }}>
            {mappingLadefehler ? (
              <div style={{ margin: '2px 16px 8px', padding: '8px 10px', border: `1px solid ${T.warnRand}`, background: T.warnGrund, borderRadius: 6, color: T.warn, fontSize: 11, lineHeight: 1.4, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ flex: 1 }}>Das gespeicherte Mapping konnte nicht geladen werden. Speichern ist gesperrt, damit es nicht überschrieben wird.</span>
                <button type="button" onClick={loadMapping} style={{ ...knopfStil(false), padding: '4px 8px' }}>Neu laden</button>
              </div>
            ) : null}
            {!mappingLadefehler && mappingGeladenFuer === stateKey && automatedMappingPlaceholders.length > 0 && belegteFelder(mitGrundzuordnung(activeContract.key, mappingDraft)) === 0 ? (
              <div style={{ margin: '2px 16px 8px', padding: '8px 10px', border: `1px solid ${T.warnRand}`, background: T.warnGrund, borderRadius: 6, color: T.warn, fontSize: 11, lineHeight: 1.4 }}>
                Keine Zuordnung gespeichert. Unter „Frühere Stände“ lässt sich ein älterer Stand zurückholen, oder „Vorschläge einsetzen“ füllt die Felder aus Bitrix.
              </div>
            ) : null}
            {!mappingLadefehler && mappingGeladenFuer === stateKey && mappingVorschlag.eingesetzt.length > 0 ? (
              <div style={{ padding: '0 16px 6px', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ flex: 1, color: T.muted, fontSize: 10.5, lineHeight: 1.4 }}>
                  Für {mappingVorschlag.eingesetzt.length} leere Felder gibt es einen Vorschlag aus Bitrix. Belegte Felder bleiben unverändert.
                </span>
                <button
                  type="button"
                  onClick={handleVorschlaegeEinsetzen}
                  title={mappingVorschlag.eingesetzt.map((name) => `{{${name}}} → ${mappingVorschlag.felder[name]}`).join('\n')}
                  style={{ ...knopfStil(false), padding: '4px 8px' }}
                >
                  Vorschläge einsetzen
                </button>
              </div>
            ) : null}
            {isAuthenticated ? (
              <div style={{ padding: '0 16px 6px' }}>
                <button type="button" onClick={handleToggleFruehereStaende} aria-expanded={fruehereStaendeOffen} style={textKnopfStil}>
                  {fruehereStaendeOffen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                  <RotateCcw size={11} />
                  Frühere Stände
                </button>
                {fruehereStaendeOffen ? (
                  <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <div style={{ color: T.muted, fontSize: 10.5, lineHeight: 1.4 }}>
                      Vor jedem Speichern wird der alte Stand gesichert (die letzten zehn). Zeit = bis wann er galt.
                    </div>
                    {fruehereStaendeLaden ? (
                      <div style={{ color: T.muted, fontSize: 11 }}>Lade frühere Stände…</div>
                    ) : !fruehereStaendeListe || fruehereStaendeListe.length === 0 ? (
                      <div style={{ color: T.muted, fontSize: 11 }}>Keine früheren Stände gesichert.</div>
                    ) : fruehereStaendeListe.map((stand) => (
                      <div
                        key={stand.id}
                        title={stand.felder
                          ? Object.entries(stand.felder).filter(([, wert]) => String(wert || '').trim()).map(([name, wert]) => `{{${name}}} → ${wert}`).join('\n')
                          : 'Inhalt nicht lesbar'}
                        style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: T.body }}
                      >
                        <span style={{ flex: 1 }}>
                          bis {stand.zeit ? new Date(stand.zeit).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }) : 'unbekannt'}
                          {' · '}
                          {stand.belegt === null ? 'nicht lesbar' : `${stand.belegt} belegt`}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleStandWiederherstellen(stand)}
                          disabled={!stand.belegt || Boolean(standWirdWiederhergestellt)}
                          style={{ ...knopfStil(false), padding: '3px 7px', opacity: !stand.belegt || standWirdWiederhergestellt ? 0.5 : 1, cursor: !stand.belegt || standWirdWiederhergestellt ? 'not-allowed' : 'pointer' }}
                        >
                          {standWirdWiederhergestellt === stand.id ? '…' : 'Wiederherstellen'}
                        </button>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : null}
            {loadingTemplate ? (
              <div style={{ padding: '12px 16px', color: '#64748B', fontSize: 12 }}>Lade Vorlage…</div>
            ) : mappingListe.length === 0 ? (
              <div style={{ padding: '12px 16px', color: '#64748B', fontSize: 12 }}>Keine Platzhalter (<code>{'{{...}}'}</code>) in der Vorlage gefunden.</div>
            ) : mappingListe.map(({ name: placeholder, inVorlage }) => (
              <div key={placeholder} style={{ padding: '6px 16px', display: 'flex', flexDirection: 'column', gap: 2 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <label style={{ flex: 1, color: '#0F172A', fontSize: 12, fontWeight: 700, fontFamily: 'monospace', overflowWrap: 'anywhere' }}>{`{{${placeholder}}}`}</label>
                  {!inVorlage ? (
                    <button
                      type="button"
                      onClick={() => handleRemoveStaleField(placeholder)}
                      title={`Zuordnung für „${placeholder}" entfernen (erst mit „Speichern“ endgültig)`}
                      style={{ width: 22, height: 22, padding: 0, border: '1px solid #E7E5E4', borderRadius: 4, background: '#FFFFFF', color: '#92400E', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                    >
                      <XIcon size={11} />
                    </button>
                  ) : null}
                </div>
                {placeholder === LAGEPLAN_PLACEHOLDER ? (
                  <span style={{ color: T.muted, fontSize: 10.5, lineHeight: 1.4 }}>Bild der Anlage 2 (Lageplan) – Datei-Feld aus Bitrix</span>
                ) : null}
                {!inVorlage ? (
                  <span style={{ color: T.warn, fontSize: 10.5, lineHeight: 1.4 }}>Steht nicht in der geladenen Vorlage – bleibt gespeichert, bis du ✕ drückst.</span>
                ) : null}
                {grundzuordnungFuer(activeContract.key, placeholder) && !String(mappingDraft[placeholder] || '').trim() ? (
                  <span style={{ color: T.muted, fontSize: 10.5, lineHeight: 1.4 }}>Fest zugeordnet: {grundzuordnungFuer(activeContract.key, placeholder)} – gilt, solange hier nichts steht.</span>
                ) : null}
                <input
                  list="bitrix-field-hints"
                  value={mappingDraft[placeholder] || ''}
                  onChange={(e) => handleMappingChange(placeholder, e.target.value)}
                  placeholder={grundzuordnungFuer(activeContract.key, placeholder) || 'z.B. DEAL.TITLE'}
                  style={{ padding: '6px 8px', border: '1px solid #E2E8F0', borderRadius: 6, fontSize: 12, fontFamily: 'monospace' }}
                />
              </div>
            ))}
          </div>
          ) : null}
          {/* Ausserhalb des eingeklappten Blocks: Das Lageplan-Feld nutzt die
              Vorschlagsliste auch. */}
          <datalist id="bitrix-field-hints">
            {FIELD_HINTS.map((hint) => (<option key={hint} value={hint} />))}
          </datalist>

          <div style={{ margin: '12px 16px', border: `1px solid ${T.randWeich}`, borderRadius: 8, background: '#FFFFFF', overflow: 'hidden' }}>
            <button
              type="button"
              onClick={() => setAttachmentsOpen((open) => !open)}
              style={{ width: '100%', padding: '8px 10px', border: 'none', borderBottom: attachmentsOpen ? '1px solid #E2E8F0' : 'none', background: '#FFFFFF', display: 'flex', alignItems: 'center', gap: 6, color: '#475569', fontSize: 11, fontWeight: 700, cursor: 'pointer', textAlign: 'left' }}
              title="PDF-Anlagen ein- oder ausklappen"
            >
              {attachmentsOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
              <Archive size={13} />
              <span style={{ flex: 1 }}>PDF-Anlagen</span>
              {attachmentPayload.length ? <span style={{ color: '#0F766E' }}>({attachmentPayload.length})</span> : null}
            </button>
            {attachmentsOpen ? (
            <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={handleAddLooseAttachment}
                  style={{ padding: '5px 8px', border: '1px solid #CBD5E1', borderRadius: 7, background: '#F8FAFC', color: '#122A5C', fontSize: 11, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                  title="Weitere Anlage hinzufügen – PDF, PNG oder JPG, wird beim Hochladen an die Vertrags-PDF angehängt"
                >
                  <Plus size={12} />
                  Weitere
                </button>
              </div>
              {isKleinspeicherPurchase ? (
                <div style={{ border: '1px solid #BFDBFE', borderRadius: 8, background: '#F0F7FF', padding: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <label
                    title={`Der Kaufvertrag verweist auf den Pachtvertrag als Anlage ${KLEINSPEICHER_LEASE_ANNEX_NUMBER}. Er entsteht aus derselben Vorlage wie der eigenständige Pachtvertrag, mit den Daten desselben Deals.`}
                    style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: 11, fontWeight: 700, color: '#122A5C', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={leaseAnnexEnabled}
                      onChange={(event) => setLeaseAnnexEnabled(event.target.checked)}
                      style={{ marginTop: 2 }}
                    />
                    <span>Anlage {KLEINSPEICHER_LEASE_ANNEX_NUMBER}: Pachtvertrag Kleinspeicher anhängen</span>
                  </label>
                  {leaseAnnexEnabled ? (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#334155' }}>
                        <span style={{ width: 118, flexShrink: 0 }}>Zahlweise</span>
                        <select
                          value={leaseAnnexContractKey}
                          onChange={(event) => setLeaseAnnexContractKey(event.target.value)}
                          style={{ flex: 1, minHeight: 28, padding: '4px 7px', border: '1px solid #CBD5E1', borderRadius: 7, fontSize: 11, background: '#FFFFFF' }}
                        >
                          <option value={KLEINSPEICHER_LEASE_CONTRACT_KEY_JZ}>Jahreszahlung</option>
                          <option value={KLEINSPEICHER_LEASE_CONTRACT_KEY_EZ}>Einmalzahlung</option>
                        </select>
                      </div>
                      {leaseAnnexRequirements.requiresAnnual ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#334155' }}>
                          <span style={{ width: 118, flexShrink: 0 }}>Pacht jährlich/kW</span>
                          <input
                            value={leaseAnnexNumbers.annualPerKw}
                            onChange={(event) => setLeaseAnnexNumbers((current) => ({ ...current, annualPerKw: event.target.value }))}
                            placeholder="z. B. 25"
                            style={{ width: 92, minHeight: 28, padding: '5px 7px', border: '1px solid #CBD5E1', borderRadius: 7, fontSize: 11 }}
                          />
                        </div>
                      ) : null}
                      {leaseAnnexRequirements.requiresOneTime ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#334155' }}>
                          <span style={{ width: 118, flexShrink: 0 }}>Pacht einmalig/kW</span>
                          <input
                            value={leaseAnnexNumbers.oneTimePerKw}
                            onChange={(event) => setLeaseAnnexNumbers((current) => ({ ...current, oneTimePerKw: event.target.value }))}
                            placeholder="z. B. 250"
                            style={{ width: 92, minHeight: 28, padding: '5px 7px', border: '1px solid #CBD5E1', borderRadius: 7, fontSize: 11 }}
                          />
                        </div>
                      ) : null}
                      {leaseAnnexRequirements.requiresExtensionShare ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#334155' }}>
                          <span style={{ width: 118, flexShrink: 0 }}>% in Verlängerung</span>
                          <input
                            value={leaseAnnexNumbers.extensionSharePercent}
                            onChange={(event) => setLeaseAnnexNumbers((current) => ({ ...current, extensionSharePercent: event.target.value }))}
                            placeholder="z. B. 80"
                            style={{ width: 92, minHeight: 28, padding: '5px 7px', border: '1px solid #CBD5E1', borderRadius: 7, fontSize: 11 }}
                          />
                        </div>
                      ) : null}
                      {missingLeaseAnnexNumbers.length ? (
                        <div style={{ color: '#B45309', fontSize: 10.5, lineHeight: 1.35 }}>
                          Ohne diese Angaben lässt sich der Vertrag nicht erzeugen: {missingLeaseAnnexNumbers.join(", ")}.
                        </div>
                      ) : null}
                    </>
                  ) : null}
                </div>
              ) : null}
              {attachmentRows.length === 0 ? (
                <div style={{ color: T.muted, fontSize: 10.5, lineHeight: 1.4 }}>
                  Die Vorlage hat kein Anlagenverzeichnis. Eigene Anlagen über „Weitere“.
                </div>
              ) : attachmentRows.map((row) => {
                const attachment = row.attachment || null;
                const rowLabel = attachment?.label || row.label || 'Anlage';
                return (
                  <div key={row.id} style={{ border: '1px solid #E2E8F0', borderRadius: 8, background: '#F8FAFC', padding: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <input
                        value={attachment?.anlageNumber ?? row.anlageNumber ?? ''}
                        onChange={(event) => {
                          const existing = attachment || { id: row.id, label: rowLabel, filename: '', mimeType: '', bytes: 0, base64: '' };
                          setContractAttachments((current) => {
                            const next = { ...existing, anlageNumber: Number(event.target.value) || '' };
                            return current.some((item) => item.id === row.id)
                              ? current.map((item) => (item.id === row.id ? next : item))
                              : [...current, next];
                          });
                        }}
                        placeholder="Nr."
                        style={{ width: 46, minHeight: 28, padding: '5px 6px', border: '1px solid #CBD5E1', borderRadius: 7, fontSize: 11 }}
                      />
                      <input
                        value={rowLabel}
                        onChange={(event) => {
                          const existing = attachment || { id: row.id, anlageNumber: row.anlageNumber || null, filename: '', mimeType: '', bytes: 0, base64: '' };
                          setContractAttachments((current) => {
                            const next = { ...existing, label: event.target.value };
                            return current.some((item) => item.id === row.id)
                              ? current.map((item) => (item.id === row.id ? next : item))
                              : [...current, next];
                          });
                        }}
                        placeholder="Bezeichnung der Anlage"
                        style={{ flex: 1, minHeight: 28, padding: '5px 7px', border: '1px solid #CBD5E1', borderRadius: 7, fontSize: 11 }}
                      />
                      {!row.fromAnnexList || attachment ? (
                        <button
                          type="button"
                          onClick={() => handleRemoveAttachment(row.id)}
                          title="Anlage entfernen"
                          style={{ width: 28, height: 28, border: '1px solid #FECACA', borderRadius: 7, background: '#FEF2F2', color: '#B91C1C', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                        >
                          <Trash2 size={12} />
                        </button>
                      ) : null}
                    </div>
                    {bitrixDateienProNummer[attachment?.anlageNumber ?? row.anlageNumber] ? (
                      <div style={{ color: '#0F766E', fontSize: 10.5 }}>
                        Aus Bitrix: {bitrixDateienProNummer[attachment?.anlageNumber ?? row.anlageNumber]} Datei(en), Auswahl oben unter „Anlagen“.
                      </div>
                    ) : null}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <input
                        type="file"
                        accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
                        onChange={(event) => {
                          const file = event.target.files && event.target.files[0];
                          event.target.value = '';
                          handleAttachmentFileChange({ id: row.id, anlageNumber: attachment?.anlageNumber ?? row.anlageNumber, label: rowLabel }, file);
                        }}
                        style={{ flex: 1, fontSize: 10.5 }}
                      />
                    </div>
                    {attachment?.filename ? (
                      <div style={{ color: '#0F766E', fontSize: 10.5, lineHeight: 1.35, overflowWrap: 'anywhere' }}>
                        {attachment.filename} · {(Number(attachment.bytes || 0) / 1024 / 1024).toFixed(2)} MB
                      </div>
                    ) : (
                      <div style={{ color: '#94A3B8', fontSize: 10.5 }}>Keine Datei ausgewählt.</div>
                    )}
                  </div>
                );
              })}
            </div>
            ) : null}
          </div>

        </aside>
      </div>
    </div>
  );
}
